/**
 * =============================================================================
 * IDS CRM Web - Ticket Detail Page
 * =============================================================================
 *
 * PURPOSE:
 * Displays ticket details with work logs, assignment history, and actions.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import DeleteRecordButton, { canDeleteRecords } from '../components/DeleteRecordButton';
import { useAuth } from '../context/AuthContext';
import { filesToAttachmentPayload, savedAttachmentsOnly } from '../utils/attachments';
import { formatDate, toLocalDateTimeInput } from '../utils/format';
import { formatMoney } from '../utils/money';
import { openDraft } from '../utils/emailDraft';
import EmailDraftModal from '../components/EmailDraftModal';
import { authedFileHref } from '../utils/files';
import TemplateCheckFormRenderer, { validateAnswers } from '../components/TemplateCheckFormRenderer';
import JobStagesCard, { ARRIVED_HOME_LABEL, FINISH_STAGES_HINT, jobStagesProblem } from '../components/JobStagesCard';
import { parseFaultCodes, formatFaultCodes, faultCodesFromOtherLogs } from '../utils/faultCodes';
import RelatedBulletins from '../components/RelatedBulletins';
import EngineerPicker from '../components/EngineerPicker';
import MoveLogModal from '../components/MoveLogModal';
import ConfirmHoursModal from '../components/ConfirmHoursModal';
import WorkLogOutcomeSection, { outcomeClosesWork } from '../components/WorkLogOutcomeSection';
import {
  reopenActions, cancelReopenRequest, ReopenStatusLine, WorkLogChanges,
  RequestReopenModal, DecideReopenModal, ReopenLogModal, UpdateWorkLogModal,
  SAVED_STILL_REOPENED_MESSAGE, COMPLETED_AGAIN_MESSAGE
} from '../components/WorkLogReopen';
import { useConfirm } from '../components/ConfirmDialog';
import SparePartsUsed from '../components/SparePartsUsed';
import PartsUsedList, { readPartsUsed } from '../components/PartsUsedList';
import WorkLogFormViewer, { WorkLogFormList } from '../components/WorkLogFormViewer';
import useTemplateCheckForm from '../hooks/useTemplateCheckForm';
import { newUuid } from '../hooks/useSavedLogWork';
import { useSparePartsList } from '../utils/sparePartsList';
import { firstSpareProblem, partsSummaryForLog, quantityText, spareLineLabel } from '../utils/spareLines';
import { isOperationalReadOnly, canFitParts } from '../utils/permissions';
import ReviewNotes from '../components/ReviewNotes';
import './TicketDetail.css';
import OpsBoardServiceStatus from '../components/OpsBoardServiceStatus';
import { formOwnsCompletion, completionTicked } from '../utils/formCompletion';

// A ticket is opened from Jobs & Tickets and from Scheduling: its "←" link
// goes back to the list it was opened from, as that list was left.
const TICKET_LISTS = { '/tickets': 'Jobs & Tickets', '/tasks': 'Scheduling' };

const isBrushRobotType = (robotType) => {
  if (!robotType || typeof robotType !== 'string') return false;
  return robotType.toLowerCase().includes('brush');
};

const BRUSH_ROBOT_CHECK_FIELDS = [
  ['cell_guarding_secure', 'Is robot cell guarding secure and damage free?'],
  ['air_regulator_ok', 'Check air regulator has sufficient air motor oil and pressure.'],
  ['base_plate_secure', 'Check all base plate connections are secure and damage free.'],
  ['tof_camera_secure', 'Check TOF camera and ensure connections are secure.'],
  ['eoat_frame_ok', 'Is there any excessive damage to the EOAT frame?', true],
  ['bolts_secure', 'Check all tool and robot bolts are secure.'],
  ['bearing_gearbox_ok', 'Inspect front bearing, gearbox and bushes.'],
  ['eoat_functions_ok', 'Check operation of all EOAT functions work properly.'],
  ['strut_ok', 'Check strut works correctly.'],
  ['end_cap_secure', 'Is the end cap strong/secure enough to avoid injury to cows?'],
  ['robot_bag_ok', 'Is the robot bag free of holes and relatively clean?'],
  ['drench_ok', 'Does the drench operate correctly and clean the tool efficiently?'],
  ['system_efficient', 'Does the system perform efficiently?'],
  ['remote_access_ok', 'Can you access TeamViewer, TOFs, HMI and Virtual Pendant without issues?'],
  ['cctv_ok', 'Are the CCTV cameras online with a clear and clean view?'],
  ['shed_office_secure', 'Check shed/office is secure and dry.'],
  ['pump_box_ok', 'Inspect Pump Box.'],
  ['sufficient_spares', 'Does the farm have sufficient spares?']
];

const emptyBrushRobotCheck = {
  cell_guarding_secure: '',
  cell_issues: '',
  air_regulator_ok: '',
  base_plate_secure: '',
  tof_camera_secure: '',
  eoat_frame_ok: '',
  bolts_secure: '',
  bearing_gearbox_ok: '',
  eoat_functions_ok: '',
  strut_ok: '',
  end_cap_secure: '',
  robot_bag_ok: '',
  drench_ok: '',
  system_efficient: '',
  remote_access_ok: '',
  cctv_ok: '',
  shed_office_secure: '',
  pump_box_ok: '',
  other_issues: '',
  sufficient_spares: '',
  check_completed: false,
  step_attachments: {}
};

const brushRobotCheckFromWorkLog = (workLog) => {
  const robotCheck = workLog?.robot_check;
  if (!robotCheck || robotCheck.form_type !== 'brush_robot_check') {
    return { ...emptyBrushRobotCheck, step_attachments: {} };
  }
  const checkData = robotCheck.check_data || {};
  const attachments = robotCheck.attachments || [];
  const stepAttachments = {};
  attachments.forEach(att => {
    if (att.step_key) {
      if (!stepAttachments[att.step_key]) {
        stepAttachments[att.step_key] = [];
      }
      stepAttachments[att.step_key].push(att);
    }
  });
  return {
    cell_guarding_secure: checkData.cell_guarding_secure ?? '',
    cell_issues: checkData.cell_issues || '',
    air_regulator_ok: checkData.air_regulator_ok ?? '',
    base_plate_secure: checkData.base_plate_secure ?? '',
    tof_camera_secure: checkData.tof_camera_secure ?? '',
    eoat_frame_ok: checkData.eoat_frame_ok ?? '',
    bolts_secure: checkData.bolts_secure ?? '',
    bearing_gearbox_ok: checkData.bearing_gearbox_ok ?? '',
    eoat_functions_ok: checkData.eoat_functions_ok ?? '',
    strut_ok: checkData.strut_ok ?? '',
    end_cap_secure: checkData.end_cap_secure ?? '',
    robot_bag_ok: checkData.robot_bag_ok ?? '',
    drench_ok: checkData.drench_ok ?? '',
    system_efficient: checkData.system_efficient ?? '',
    remote_access_ok: checkData.remote_access_ok ?? '',
    cctv_ok: checkData.cctv_ok ?? '',
    shed_office_secure: checkData.shed_office_secure ?? '',
    pump_box_ok: checkData.pump_box_ok ?? '',
    other_issues: checkData.other_issues || '',
    sufficient_spares: checkData.sufficient_spares ?? '',
    check_completed: checkData.check_completed === true,
    step_attachments: stepAttachments
  };
};

const isSprayRobotType = (robotType) => {
  if (!robotType || typeof robotType !== 'string') return false;
  return robotType.toLowerCase().includes('spray');
};

/**
 * Returns true if the asset type is neither Brush nor Spray.
 * These asset types use the generic template check path instead of
 * the hard-coded Brush/Spray robot check forms.
 */
const isGenericAssetType = (robotType) => {
  if (!robotType || typeof robotType !== 'string') return false;
  return !isBrushRobotType(robotType) && !isSprayRobotType(robotType);
};

const SPRAY_ROBOT_CHECK_FIELDS = [
  ['cell_guarding_secure', 'Is robot cell guarding secure and damage free?'],
  ['base_plate_secure', 'Check all base plate connections are secure and damage free.'],
  ['tof_camera_secure', 'Check TOF camera/s connections are secure.'],
  ['eoat_frame_ok', 'Is there any excessive damage to the EOAT frame?', true],
  ['bolts_secure', 'Check all tool bolts are secure.'],
  ['eoat_functions_ok', 'Check operation of all EOAT functions work properly.'],
  ['drench_ok', 'Is the drench operating correctly and cleaning tool efficiently?'],
  ['end_cap_secure', 'Is the end cap strong/secure enough to avoid injury to cows?'],
  ['robot_bag_ok', 'Is the robot bag free of holes and relatively clean?'],
  ['system_efficient', 'Does the system perform efficiently?'],
  ['remote_access_ok', 'Can you access TeamViewer, TOFs, HMI and Virtual Pendant without issues?'],
  ['cctv_ok', 'Are the CCTV cameras online with a clear and clean view?'],
  ['shed_office_secure', 'Check shed/office is secure and dry.'],
  ['pump_box_ok', 'Inspect Pump Box.'],
  ['sufficient_spares', 'Does the farm have sufficient spares?']
];

const emptySprayRobotCheck = {
  cell_guarding_secure: '',
  cell_issues: '',
  base_plate_secure: '',
  tof_camera_secure: '',
  eoat_frame_ok: '',
  bolts_secure: '',
  eoat_functions_ok: '',
  drench_ok: '',
  end_cap_secure: '',
  robot_bag_ok: '',
  system_efficient: '',
  remote_access_ok: '',
  cctv_ok: '',
  shed_office_secure: '',
  pump_box_ok: '',
  sufficient_spares: '',
  other_issues: '',
  check_completed: false,
  step_attachments: {}
};

const sprayRobotCheckFromWorkLog = (workLog) => {
  const robotCheck = workLog?.robot_check;
  if (!robotCheck || robotCheck.form_type !== 'spray_robot_check') {
    return { ...emptySprayRobotCheck, step_attachments: {} };
  }
  const checkData = robotCheck.check_data || {};
  const attachments = robotCheck.attachments || [];
  const stepAttachments = {};
  attachments.forEach(att => {
    if (att.step_key) {
      if (!stepAttachments[att.step_key]) {
        stepAttachments[att.step_key] = [];
      }
      stepAttachments[att.step_key].push(att);
    }
  });
  return {
    cell_guarding_secure: checkData.cell_guarding_secure ?? '',
    cell_issues: checkData.cell_issues || '',
    base_plate_secure: checkData.base_plate_secure ?? '',
    tof_camera_secure: checkData.tof_camera_secure ?? '',
    eoat_frame_ok: checkData.eoat_frame_ok ?? '',
    bolts_secure: checkData.bolts_secure ?? '',
    eoat_functions_ok: checkData.eoat_functions_ok ?? '',
    drench_ok: checkData.drench_ok ?? '',
    end_cap_secure: checkData.end_cap_secure ?? '',
    robot_bag_ok: checkData.robot_bag_ok ?? '',
    system_efficient: checkData.system_efficient ?? '',
    remote_access_ok: checkData.remote_access_ok ?? '',
    cctv_ok: checkData.cctv_ok ?? '',
    shed_office_secure: checkData.shed_office_secure ?? '',
    pump_box_ok: checkData.pump_box_ok ?? '',
    sufficient_spares: checkData.sufficient_spares ?? '',
    other_issues: checkData.other_issues || '',
    check_completed: checkData.check_completed === true,
    step_attachments: stepAttachments
  };
};

const DAILY_VAN_CHECK_BOOLEAN_FIELDS = [
  ['warning_lights_messages', 'Warning lights/messages'],
  ['adblue_required', 'AdBlue required'],
  ['oil_sufficient', 'Oil sufficient'],
  ['screen_wash_sufficient', 'Screen wash sufficient'],
  ['lights_working', 'Lights working'],
  ['tyre_pressure_correct', 'Tyre pressure correct'],
  ['tyre_tread_sufficient', 'Tyre tread sufficient'],
  ['damage', 'Vehicle damage']
];

const emptyDailyVanCheck = {
  vehicle_id: '',
  seat_selections: [],
  current_odometer_reading: '',
  warning_lights_messages: '',
  warning_notes: '',
  adblue_required: '',
  oil_sufficient: '',
  screen_wash_sufficient: '',
  lights_working: '',
  tyre_pressure_correct: '',
  tyre_tread_sufficient: '',
  damage: '',
  damage_description: '',
  damage_photo_attachments: [],
  additional_information: ''
};

const toDateTimeInput = (value) => {
  if (!value) return '';
  return toLocalDateTimeInput(value);
};

// Parts used lines in the ticket's Log Work window (spare parts, Push 2) are
// kept on the phone until the work log saves, so they survive no signal and a
// reload as job lines do: one localStorage entry per user, ticket and log
// ('new' for a new log), { savedAt, lines }. The rest of the window lives only
// in the window, as before. A line keeps its id for life, so a restored line
// that was in fact saved is skipped by the server on the same log, never
// recorded twice.
const TICKET_SPARES_PREFIX = 'ids-crm-ticket-spares-v1';
const TICKET_SPARES_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const RESTORED_SPARES_NOTE = 'Parts used you added here earlier, and did not save, are shown again. Remove any you do not want.';
// Continuing a log when the ticket's Parts used could not be read: the parts
// already recorded on it are not known, so say so rather than show none.
const RECORDED_SPARES_UNREAD_NOTE = 'The parts already recorded on this work log could not be loaded. Check the ticket\'s Parts used before adding a part again.';

const ticketSparesKey = (userId, ticketId, logId) => `${TICKET_SPARES_PREFIX}:${userId || 'unknown'}:${ticketId}:${logId || 'new'}`;

/** The lines this phone holds for a ticket's work log, or [] (older than 14 days are dropped). */
function readTicketSpares(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const stored = JSON.parse(raw);
    const lines = stored && Array.isArray(stored.lines) ? stored.lines : [];
    if (!(Date.now() - Number(stored.savedAt) <= TICKET_SPARES_MAX_AGE_MS)) {
      window.localStorage.removeItem(key);
      return [];
    }
    return lines.filter(line => line && typeof line.key === 'string' && line.value && typeof line.value === 'object');
  } catch (err) {
    return [];
  }
}

// A blocked or full store just keeps the lines in the window.
function writeTicketSpares(key, lines) {
  try {
    if (lines.length === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), lines }));
    }
  } catch (err) {
    // Kept in memory only.
  }
}

function forgetTicketSpares(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    // Nothing to forget.
  }
}

const booleanToSelectValue = (value) => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
};

const dailyVanCheckFromWorkLog = (workLog) => {
  const vehicleCheck = workLog?.vehicle_check;
  const checkData = vehicleCheck?.check_data || {};
  if (!vehicleCheck && workLog?.log_type !== 'site_work') return { ...emptyDailyVanCheck };

  return {
    ...emptyDailyVanCheck,
    vehicle_id: vehicleCheck?.vehicle_id || checkData.vehicle_id || '',
    seat_selections: Array.isArray(checkData.employees_present_user_ids)
      ? checkData.employees_present_user_ids
      : [],
    current_odometer_reading: vehicleCheck?.odometer_reading ?? checkData.current_odometer_reading ?? '',
    warning_lights_messages: booleanToSelectValue(checkData.warning_lights_messages),
    warning_notes: checkData.warning_notes || '',
    adblue_required: booleanToSelectValue(checkData.adblue_required),
    oil_sufficient: booleanToSelectValue(checkData.oil_sufficient),
    screen_wash_sufficient: booleanToSelectValue(checkData.screen_wash_sufficient),
    lights_working: booleanToSelectValue(checkData.lights_working),
    tyre_pressure_correct: booleanToSelectValue(checkData.tyre_pressure_correct),
    tyre_tread_sufficient: booleanToSelectValue(checkData.tyre_tread_sufficient),
    damage: booleanToSelectValue(checkData.damage),
    damage_description: checkData.damage_description || '',
    damage_photo_attachments: Array.isArray(vehicleCheck?.attachments) ? vehicleCheck.attachments : [],
    additional_information: checkData.additional_information || ''
  };
};

const initialWorkLogForm = (workLog, defaultWorkType, defaultWorkPeriod, defaultStartedAt) => ({
  log_type: workLog?.log_type || defaultWorkType,
  // First log on a ticket starts when the call came in, not "now" - the
  // desk engineer answering the phone shouldn't retype the same moment.
  started_at: toDateTimeInput(workLog?.started_at) || defaultStartedAt || toLocalDateTimeInput(),
  ended_at: toDateTimeInput(workLog?.ended_at),
  work_period: workLog?.work_period || defaultWorkPeriod,
  notes: workLog?.notes || '',
  outcome: workLog?.outcome || '',
  attachments: Array.isArray(workLog?.attachments) ? workLog.attachments : [],
  daily_van_check: dailyVanCheckFromWorkLog(workLog),
  brush_robot_check: brushRobotCheckFromWorkLog(workLog),
  spray_robot_check: sprayRobotCheckFromWorkLog(workLog),
  assign_next_type: '',
  assigned_to_next_id: '',
  resolution_summary: '',
  // Editing shows the log's OWN codes so they can be corrected or cleared.
  // Blank, they were invisible, and typing a code silently replaced them. A new
  // log still starts blank (Gareth 2026-09-11).
  fault_codes: formatFaultCodes(workLog?.fault_codes),
  // Other engineers who were on this job (Gareth 2026-09-11). Create only —
  // each gets their own linked log, so this is not an edit-time field.
  additional_engineer_ids: [],
  // Only used when the crew outgrows the first van's seats.
  second_van: null,
  root_cause: '',
  fault_code: '',
  customer_satisfied: false,
  satisfaction_notes: '',
  follow_up_ticket_required: false,
  follow_up_subject: '',
  follow_up_description: '',
  follow_up_priority: ''
});

function TicketDetail() {
  const { id } = useParams();
  const { authFetch, user } = useAuth();
  // RR3: operations coordinators and financial read the workspace —
  // no work logs, edits or closure (the API denies them server-side too).
  const readOnlyWorkspace = isOperationalReadOnly(user?.role);
  // The phone's copy of the spare parts list, for the Log Work picker. Fetched
  // here, not when the window opens, so it is ready with no signal.
  const spareParts = useSparePartsList({ authFetch, enabled: Boolean(user && canFitParts(user.role)) });

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A link ending ?tab=work (a message about a work log, a row on Today)
  // opens the ticket on its Work Logs tab.
  const { search } = useLocation();
  const linkedToWorkTab = new URLSearchParams(search).get('tab') === 'work';
  const [activeTab, setActiveTab] = useState(linkedToWorkTab ? 'work' : 'details');
  useEffect(() => {
    if (linkedToWorkTab) setActiveTab('work');
  }, [id, linkedToWorkTab]);
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  // Delete (managers and admins, Gareth 2026-10-13): after it, back to the
  // list as it was left; when the server refuses, its reason is shown.
  const backToList = useBackToList('/tickets', ['/tasks']);
  const [deleteRefused, setDeleteRefused] = useState(null);
  // The two audited work-log actions, each gated behind a written reason.
  const [crewModalLog, setCrewModalLog] = useState(null);
  // Reopening a completed work log to update it (Gareth 2026-10-13): the
  // window open on one log ({ kind, log }), what the last step said, and a
  // count that makes an open Changes list read again.
  const [reopenWindow, setReopenWindow] = useState(null);
  const [reopenMessage, setReopenMessage] = useState(null);
  const [changesKey, setChangesKey] = useState(0);
  const { confirmElement, requestConfirm } = useConfirm();
  // Moving work to the ticket being kept — the way out of a duplicate ticket
  // without losing the engineer's call-out pay (Gareth 2026-09-15).
  const [moveModalLog, setMoveModalLog] = useState(null);
  const [moveMessage, setMoveMessage] = useState(null);
  // B4: what the API says about a call-out saved into a month that has already
  // been paid - shown to the engineer who saved it, not swallowed.
  const [payrollNotice, setPayrollNotice] = useState(null);
  // Phase 6 step 0f: a co-engineer confirming their own crew entry.
  const [confirmLog, setConfirmLog] = useState(null);
  const [showEmailCustomerModal, setShowEmailCustomerModal] = useState(false);
  const [editingWorkLog, setEditingWorkLog] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [workLogConfig, setWorkLogConfig] = useState({ work_types: [], work_periods: [], outcomes: [] });
  const [partsUsed, setPartsUsed] = useState([]);
  // The forms filled in on this ticket's work logs (robot checks, van checks,
  // site forms), listed under each log, and the one open in the viewer.
  const [logForms, setLogForms] = useState([]);
  const [openFormId, setOpenFormId] = useState(null);
  // 'loading' while the latest read of the ticket's Parts used is out, then
  // 'ready' or 'failed'. Only the latest read counts.
  const [partsStatus, setPartsStatus] = useState('loading');
  const partsReadRef = useRef(0);
  const [costs, setCosts] = useState(null);
  const [costsLoading, setCostsLoading] = useState(false);

  // Fault linking + reopen (2026-08-11). Available to every role — anyone
  // who can create a ticket can link or reopen one; the API enforces it.
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkable, setLinkable] = useState({ loading: false, scope: 'asset', tickets: [] });
  const [linkSaving, setLinkSaving] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenForm, setReopenForm] = useState({ reason: '', assigned_to_id: '' });
  const [reopenSaving, setReopenSaving] = useState(false);
  const [reopenError, setReopenError] = useState(null);

  const openLinkModal = async () => {
    setShowLinkModal(true);
    setLinkable({ loading: true, scope: 'asset', tickets: [] });
    try {
      const response = await authFetch(`/api/v1/tickets/${id}/linkable`);
      const data = await response.json();
      setLinkable({ loading: false, scope: data.scope || 'asset', tickets: data.data || [] });
    } catch (err) {
      console.error('Failed to load linkable tickets:', err);
      setLinkable({ loading: false, scope: 'asset', tickets: [] });
    }
  };

  const saveLink = async (previousTicketId) => {
    setLinkSaving(true);
    try {
      const response = await authFetch(`/api/v1/tickets/${id}/link`, {
        method: 'POST',
        body: JSON.stringify({ previous_ticket_id: previousTicketId })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update link');
      }
      setShowLinkModal(false);
      fetchTicket();
    } catch (err) {
      console.error('Failed to update ticket link:', err);
    } finally {
      setLinkSaving(false);
    }
  };

  const submitReopen = async (event) => {
    event.preventDefault();
    setReopenSaving(true);
    setReopenError(null);
    try {
      const response = await authFetch(`/api/v1/tickets/${id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({
          reason: reopenForm.reason,
          assigned_to_id: reopenForm.assigned_to_id || undefined
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to reopen ticket');
      }
      setShowReopenModal(false);
      setReopenForm({ reason: '', assigned_to_id: '' });
      fetchTicket();
    } catch (err) {
      setReopenError(err.message);
    } finally {
      setReopenSaving(false);
    }
  };

  // Batch C: on-demand cost rollup (manager/admin only)
  const fetchCosts = async () => {
    setCostsLoading(true);
    try {
      const response = await authFetch(`/api/v1/tickets/${id}/costs`);
      if (response.ok) {
        setCosts(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch ticket costs:', err);
    } finally {
      setCostsLoading(false);
    }
  };

  // Parts used on this ticket, every page of them. A read that fails is
  // shown as failed (never as "No parts recorded.") with Try again.
  const fetchPartsUsed = useCallback(async () => {
    const read = partsReadRef.current + 1;
    partsReadRef.current = read;
    setPartsStatus('loading');
    const rows = await readPartsUsed(authFetch, { ticketId: id });
    if (read !== partsReadRef.current) return;
    if (rows) setPartsUsed(rows);
    setPartsStatus(rows ? 'ready' : 'failed');
  }, [id, authFetch]);

  // A read that fails leaves the list as it was: the page never depends on it.
  const fetchLogForms = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/work-logs/forms?ticket_id=${id}`);
      if (!response || !response.ok) return;
      const data = await response.json();
      if (Array.isArray(data?.data)) setLogForms(data.data);
    } catch (err) {
      // Kept as it was.
    }
  }, [id, authFetch]);

  const fetchTicket = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/tickets/${id}`);

      if (!response.ok) {
        throw new Error('Ticket not found');
      }

      const data = await response.json();
      setTicket(data);

      fetchLogForms();
      // Parts used on this ticket - non-fatal if unavailable
      await fetchPartsUsed();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, authFetch, fetchPartsUsed, fetchLogForms]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  // A Parts used read that failed is tried again when the phone is back online.
  const partsStatusRef = useRef(partsStatus);
  partsStatusRef.current = partsStatus;
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const online = () => { if (partsStatusRef.current === 'failed') fetchPartsUsed(); };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, [fetchPartsUsed]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [configRes, engineersRes] = await Promise.all([
          authFetch('/api/v1/support/work-log-config'),
          authFetch('/api/v1/tickets/active-engineers')
        ]);
        if (configRes.ok) setWorkLogConfig(await configRes.json());
        if (engineersRes.ok) {
          const data = await engineersRes.json();
          setEngineers(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load work log config:', err);
      }
    };
    fetchConfig();
  }, [authFetch]);

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins}m`;
    return `${hrs}h ${mins}m`;
  };

  const isBreached = (dueDate, completedDate) => {
    if (!dueDate) return false;
    const checkDate = completedDate ? new Date(completedDate) : new Date();
    return checkDate > new Date(dueDate);
  };

  if (loading) {
    return <div className="loading-page">Loading ticket...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/tickets" also={['/tasks']} labels={TICKET_LISTS} className="btn-secondary">← Jobs &amp; Tickets</BackToList>
      </div>
    );
  }

  // Details tab: every code on the ticket, and which visit found which. Read
  // from the logs rather than ticket.fault_codes so the breakdown and the total
  // can never disagree on screen.
  const faultCodesByLog = (ticket?.work_logs || [])
    .map(log => ({
      log_id: log.log_id,
      started_at: log.started_at,
      log_type: log.log_type || 'work',
      engineer_name: log.engineer_name,
      codes: parseFaultCodes(log.fault_codes)
    }))
    .filter(entry => entry.codes.length > 0);
  const ticketFaultCodes = [...new Set(faultCodesByLog.flatMap(entry => entry.codes))].sort();

  // Crew and the three audited actions on a work log — change crew, reopen and
  // move (Gareth 2026-09-11, move added 2026-09-15). These
  // mirror services/workLogLock.js — the server is the authority and refuses
  // anyway, but showing a button that always 400s is worse than hiding it.
  const isManagerRole = ['manager', 'admin'].includes(user?.role);
  const crewFor = (log) => {
    if (!log.work_log_group_id) return [log];
    return (ticket.work_logs || []).filter(l => l.work_log_group_id === log.work_log_group_id);
  };
  // Crew changes key on the LEAD's log being open — Gareth: "crew changes while
  // the lead's log is open", so signing off ends them for the whole job. A
  // completed log's crew is corrected only by a manager, and only while the
  // log is reopened (Gareth 2026-10-13: "needs manager approval").
  const canChangeCrew = (log) => {
    if (readOnlyWorkspace) return false;
    const isLead = log.work_log_group_id ? log.log_id === log.work_log_group_id : true;
    if (!isLead) return false;
    if (log.outcome) return isManagerRole && log.reopen?.status === 'open';
    return log.engineer_id === user?.userId || isManagerRole;
  };
  // Reopening a completed log to update it: which buttons this person gets.
  const reopenFor = (log) => reopenActions(log, user, { readOnly: readOnlyWorkspace });
  const logPath = (log) => `/api/v1/tickets/${id}/work-log/${log.log_id}`;
  const reopenStepDone = (message) => {
    setReopenWindow(null);
    setReopenMessage(message);
    setChangesKey(count => count + 1);
    fetchTicket();
  };
  const cancelReopen = (log) => requestConfirm({
    title: 'Cancel this request?',
    message: 'Your request to reopen this work log will be withdrawn. You can ask again.',
    confirmLabel: 'Cancel request',
    cancelLabel: 'Keep it',
    onConfirm: async () => {
      try {
        await cancelReopenRequest(authFetch, logPath(log), log.reopen);
        reopenStepDone('Your request to reopen the work log was cancelled.');
      } catch (err) {
        setReopenMessage(err.message);
        fetchTicket();
      }
    }
  });
  // Managers move work between tickets; the server refuses a log inside a
  // finalised payroll period and says so.
  const canMove = () => !readOnlyWorkspace && isManagerRole;
  // Phase 6 step 0f: a co-engineer's OWN entry from someone else's crew, not
  // yet signed off. It is born with the lead's start and finish, so Continue
  // (which needs no finish) never showed and nothing could be pressed. Offered
  // on a resolved or closed ticket too - confirming touches only the hours,
  // never the ticket. The server checks the owner and the pay period anyway.
  const canConfirmLog = (log) => !readOnlyWorkspace
    && Boolean(user?.userId) && log.engineer_id === user.userId
    && Boolean(log.work_log_group_id) && log.work_log_group_id !== log.log_id
    && !log.outcome;
  const leadNameFor = (log) => (log?.work_log_group_id
    ? (ticket.work_logs || []).find(member => member.log_id === log.work_log_group_id)?.engineer_name || null
    : null);

  const slaPaused = Boolean(ticket.sla_paused_at);
  // While paused the clock is stopped (due dates shift forward on resume),
  // so a past-due date is not a live breach.
  const responseBreached = !slaPaused && isBreached(ticket.sla_response_due, ticket.first_response_at);
  const resolutionBreached = !slaPaused && isBreached(ticket.sla_resolution_due, ticket.resolved_at);

  return (
    <div className="ticket-detail">
      {/* Header Card */}
      <div className="detail-header">
        <div className="header-left">
          <div className="header-info">
            <BackToList to="/tickets" also={['/tasks']} labels={TICKET_LISTS} className="back-link">← Jobs &amp; Tickets</BackToList>
            <div className="header-row-1">
              <h1>{ticket.ticket_ref}</h1>
              <span className={`priority-chip priority-${ticket.priority}`}>
                {ticket.priority}
              </span>
              <span className={`status-chip status-${ticket.status}`}>
                {ticket.status.replace(/_/g, ' ')}
              </span>
              {ticket.reopened_at && ticket.status !== 'closed' && (
                <span className="meta-chip reopened-chip" title={ticket.reopen_reason || ''}>
                  Reopened {formatDate(ticket.reopened_at)}
                </span>
              )}
            </div>
            <div className="header-row-2">
              <span className="meta-chip">{ticket.channel}</span>
              <span className="meta-chip">{ticket.support_tier.replace(/_/g, ' ')}</span>
              {ticket.fault_category && (
                <span className="meta-chip">{ticket.fault_category.replace(/_/g, ' ')}</span>
              )}
              {ticket.previous_ticket_id && (
                <span className="meta-chip followon-chip">
                  Follow-on of {ticket.linked_chain?.find(c => c.ticket_id === ticket.previous_ticket_id)?.ticket_ref || 'a previous ticket'}
                </span>
              )}
            </div>
            <div className="header-row-3">
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                </svg>
                <Link to={`/customers/${ticket.customer_id}`} className="customer-link">{ticket.customer_name}</Link>
              </span>
              {ticket.asset_ref && (
                <span className="meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                  </svg>
                  <Link to={`/assets/${ticket.asset_id}`} className="asset-link">{ticket.asset_ref}</Link>
                </span>
              )}
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                {ticket.assigned_to_name || 'Unassigned'}
              </span>
            </div>
            {/* On the Ops Planning board, or add it (Gareth 2026-10-21). */}
            <OpsBoardServiceStatus type="ticket" id={ticket.ticket_id} />
          </div>
        </div>
        <div className="header-actions">
          {!readOnlyWorkspace && ticket.status !== 'closed' && (
            <button
              className="btn-action"
              onClick={() => {
                setEditingWorkLog(null);
                setShowWorkLogModal(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Log Work
            </button>
          )}
          {/* The Resolve and Close actions are retired (2026-07-29): tickets
              close via a work log with the Completed outcome — same
              resolution + satisfaction fields, plus the labour record. */}
          {ticket.status === 'closed' && (
            <>
              <button className="btn-action" onClick={() => setShowEmailCustomerModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16v16H4z" opacity="0"/>
                  <path d="M22 6l-10 7L2 6"/>
                  <path d="M2 6h20v12H2z"/>
                </svg>
                {ticket.closure_email_sent ? 'Email Customer Again' : 'Email Customer'}
              </button>
              {ticket.closure_email_sent && (
                <span className="closure-email-sent-badge">
                  Emailed {formatDate(ticket.closure_email_sent_at)}
                </span>
              )}
              {/* Reopen only inside the configurable window; after that the
                  follow-on path takes over. Open to anyone who can create a
                  ticket — the API enforces the same rule. */}
              {ticket.reopen_allowed_until && new Date(ticket.reopen_allowed_until) > new Date() && (
                <button className="btn-action" onClick={() => { setReopenError(null); setShowReopenModal(true); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10"/>
                    <path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                  </svg>
                  Reopen
                </button>
              )}
              <Link to={`/tickets/new?follow_on=${id}`} className="btn-action">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Raise Follow-on
              </Link>
            </>
          )}
          {/* Fault linking: available on every ticket, open or closed — the
              person handling a job often only realises mid-visit that it's a
              repeat fault. */}
          <button className="btn-action" onClick={openLinkModal}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
            </svg>
            {ticket.previous_ticket_id ? 'Change Link' : 'Link to Previous'}
          </button>
          {!readOnlyWorkspace && (
          <Link to={`/tickets/${id}/edit`} className="btn-action">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </Link>
          )}
          {/* The server refuses a ticket holding a call-out work log: its
              sentence is shown under the header. */}
          {canDeleteRecords(user?.role) && (
            <DeleteRecordButton
              kind="ticket"
              reference={ticket.ticket_ref}
              endpoint={`/api/v1/tickets/${id}`}
              authFetch={authFetch}
              onDeleted={backToList}
              onRefused={setDeleteRefused}
            />
          )}
        </div>
      </div>

      {deleteRefused && (
        <div className="delete-refused" role="alert">
          <span>{deleteRefused}</span>
          <button type="button" className="btn-small" onClick={() => setDeleteRefused(null)}>Dismiss</button>
        </div>
      )}

      {/* SLA Paused Banner */}
      {slaPaused && ticket.status !== 'closed' && (
        <div className="alert-bar info sla-paused-bar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="10" y1="9" x2="10" y2="15"/>
            <line x1="14" y1="9" x2="14" y2="15"/>
          </svg>
          <span className="alert-message">
            SLA clock paused while {ticket.status.replace(/_/g, ' ')} (since {new Date(ticket.sla_paused_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}). Due dates extend when the ticket resumes.
          </span>
        </div>
      )}

      {/* SLA Status Banner */}
      {(responseBreached || resolutionBreached) && ticket.status !== 'closed' && (
        <div className="alert-bar warning">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span className="alert-message">
            {responseBreached && !ticket.first_response_at && 'SLA Response Time Breached. '}
            {resolutionBreached && !ticket.resolved_at && 'SLA Resolution Time Breached.'}
          </span>
        </div>
      )}

      {/* Subject Card */}
      <div className="subject-card">
        <h2>{ticket.subject}</h2>
        {ticket.description && <p className="description">{ticket.description}</p>}
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={activeTab === 'details' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        {user?.role !== 'commercial' && (
        <button
          className={activeTab === 'work' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('work')}
        >
          Work Logs ({ticket.work_logs?.length || 0})
        </button>
        )}
        {['admin', 'manager', 'team_leader', 'financial', 'operations_coordinator'].includes(user?.role) && (
          <button
            className={activeTab === 'costs' ? 'tab active' : 'tab'}
            onClick={() => { setActiveTab('costs'); fetchCosts(); }}
          >
            Costs
          </button>
        )}
        {user?.role !== 'commercial' && (
        <button
          className={activeTab === 'history' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('history')}
        >
          Assignment History
        </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="detail-content">
        {activeTab === 'details' && (
          <div className="details-grid">
            {/* Customer Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                </svg>
                <h3>CUSTOMER</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Customer</span>
                  <span className="info-value highlight">
                    <Link to={`/customers/${ticket.customer_id}`}>{ticket.customer_name}</Link>
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Contact</span>
                  <span className="info-value">{ticket.contact_name || ticket.reported_by_name || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{ticket.contact_phone || ticket.reported_by_phone || '-'}</span>
                </div>
              </div>
            </div>

            {/* Asset Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                </svg>
                <h3>ASSET</h3>
              </div>
              <div className="card-body">
                {ticket.asset_id ? (
                  <>
                    <div className="info-row">
                      <span className="info-label">Asset</span>
                      <span className="info-value highlight">
                        <Link to={`/assets/${ticket.asset_id}`}>{ticket.asset_ref}</Link>
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Serial</span>
                      <span className="info-value">{ticket.serial_number || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Type</span>
                      <span className="info-value">{ticket.robot_type?.replace(/_/g, ' ') || '-'}</span>
                    </div>
                  </>
                ) : (
                  <p className="no-data">No asset linked</p>
                )}
              </div>
            </div>

            {/* SLA Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <h3>SLA</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Response Due</span>
                  <span className={`info-value ${responseBreached && !ticket.first_response_at ? 'breached' : ''}`}>
                    {formatDateTime(ticket.sla_response_due)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">First Response</span>
                  <span className={`info-value ${ticket.first_response_at ? 'ok' : ''}`}>
                    {formatDateTime(ticket.first_response_at)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Resolution Due</span>
                  <span className={`info-value ${resolutionBreached && !ticket.resolved_at ? 'breached' : ''}`}>
                    {formatDateTime(ticket.sla_resolution_due)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Resolved At</span>
                  <span className={`info-value ${ticket.resolved_at ? 'ok' : ''}`}>
                    {formatDateTime(ticket.resolved_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Assignment Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                <h3>ASSIGNMENT</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Assigned To</span>
                  <span className="info-value">{ticket.assigned_to_name || 'Unassigned'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Opened By</span>
                  <span className="info-value">{ticket.opened_by_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Opened At</span>
                  <span className="info-value">{formatDateTime(ticket.opened_at)}</span>
                </div>
                {ticket.closed_at && (
                  <div className="info-row">
                    <span className="info-label">Closed At</span>
                    <span className="info-value">{formatDateTime(ticket.closed_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Resolution Card */}
            {ticket.resolution_summary && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <h3>RESOLUTION</h3>
                </div>
                <div className="card-body">
                  <div className="info-row">
                    <span className="info-label">Type</span>
                    <span className="info-value">{ticket.resolution_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Summary</span>
                    <span className="info-value">{ticket.resolution_summary}</span>
                  </div>
                  {ticket.root_cause && (
                    <div className="info-row">
                      <span className="info-label">Root Cause</span>
                      <span className="info-value">{ticket.root_cause}</span>
                    </div>
                  )}
                  {/* Fault codes moved out of here (2026-09-11): this card only
                      renders once there is a resolution summary, so codes found
                      on an open ticket were invisible — the opposite of the
                      sharing they exist for. See the Fault Codes card below. */}
                </div>
              </div>
            )}

            {/* Fault Codes (Gareth 2026-09-11: "yes add it to the details tab").
                Renders at ANY status, because codes get recorded during the work
                rather than at the close. Shows which visit found which, since
                that attribution is the reason they live on the work log. */}
            {ticketFaultCodes.length > 0 && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <h3>FAULT CODES</h3>
                </div>
                <div className="card-body">
                  <div className="info-row">
                    <span className="info-label">All found</span>
                    <span className="info-value">
                      <span className="fault-code-chips">
                        {ticketFaultCodes.map(code => (
                          <span key={code} className="fault-code-chip">{code}</span>
                        ))}
                      </span>
                    </span>
                  </div>
                  {faultCodesByLog.map(entry => (
                    <div className="info-row" key={entry.log_id}>
                      <span className="info-label">
                        {formatDateTime(entry.started_at)}
                      </span>
                      <span className="info-value">
                        <span className="fault-code-chips">
                          {entry.codes.map(code => (
                            <span key={code} className="fault-code-chip">{code}</span>
                          ))}
                        </span>
                        <span className="fault-code-attribution">
                          {entry.log_type.replace(/_/g, ' ')}
                          {entry.engineer_name ? ` · ${entry.engineer_name}` : ''}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            <AttachmentList title="Ticket Attachments" attachments={ticket.attachments} />

            {/* Billing Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                <h3>BILLING</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Warranty</span>
                  <span className="info-value">{ticket.is_warranty ? 'Yes' : 'No'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Billable</span>
                  <span className="info-value">{ticket.is_billable ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            {/* Related Tickets — the fault chain, oldest first */}
            {Array.isArray(ticket.linked_chain) && ticket.linked_chain.length > 0 && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                  </svg>
                  <h3>RELATED TICKETS</h3>
                </div>
                <div className="card-body">
                  <p className="chain-hint">Work on the same fault, oldest first.</p>
                  {ticket.linked_chain.map(chainTicket => (
                    <div
                      key={chainTicket.ticket_id}
                      className={`chain-row ${chainTicket.ticket_id === ticket.ticket_id ? 'current' : ''}`}
                    >
                      {chainTicket.ticket_id === ticket.ticket_id ? (
                        <span className="chain-ref">{chainTicket.ticket_ref}</span>
                      ) : (
                        <Link to={`/tickets/${chainTicket.ticket_id}`} className="chain-ref">{chainTicket.ticket_ref}</Link>
                      )}
                      <span className="chain-subject">{chainTicket.subject}</span>
                      <span className={`status-chip status-${chainTicket.status}`}>
                        {chainTicket.status.replace(/_/g, ' ')}
                      </span>
                      <span className="chain-dates">
                        {formatDate(chainTicket.opened_at)}
                        {chainTicket.closed_at ? ` — ${formatDate(chainTicket.closed_at)}` : ''}
                      </span>
                      {chainTicket.ticket_id === ticket.ticket_id && (
                        <span className="chain-current-tag">This ticket</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'work' && (
          <div className="work-logs-section">
            {moveMessage && <div className="modal-hint">{moveMessage}</div>}
            {reopenMessage && (
              <div className="modal-hint" role="status">
                {reopenMessage}
                <button type="button" className="btn-small" onClick={() => setReopenMessage(null)}>Dismiss</button>
              </div>
            )}
            {payrollNotice && (
              <div className="modal-hint" role="status">
                {payrollNotice}
                <button type="button" className="btn-small" onClick={() => setPayrollNotice(null)}>Dismiss</button>
              </div>
            )}
            <div className="list-header">
              <h3>Work Logs</h3>
              {!readOnlyWorkspace && ticket.status !== 'closed' && (
                <button
                  className="btn-small"
                  onClick={() => {
                    setEditingWorkLog(null);
                    setShowWorkLogModal(true);
                  }}
                >
                  + Log Work
                </button>
              )}
            </div>
            {!ticket.work_logs || ticket.work_logs.length === 0 ? (
              <div className="empty-message">No work logged yet</div>
            ) : (
              <div className="work-logs-list">
                {ticket.work_logs.map(log => {
                  const logIsInProgress = !log.ended_at && !log.outcome;
                  // Parts are filed on the crew's lead log, so they show under the lead's card.
                  const logParts = partsSummaryForLog(partsUsed, log.log_id);
                  // The forms filled in on this log, each opening read only.
                  const formsOnLog = logForms.filter(form => form.work_log_id === log.log_id);
                  const robotFormListed = formsOnLog.some(form => !form.is_van_check);
                  const reopenOffers = reopenFor(log);
                  return (
                  <div key={log.log_id} className={`work-log-item ${logIsInProgress ? 'in-progress' : ''}`}>
                    <div className="log-header">
                      <span className="log-type">{log.log_type.replace(/_/g, ' ')}</span>
                      {logIsInProgress && <span className="status-badge in-progress">In Progress</span>}
                      {log.reopen?.status === 'requested' && <span className="status-badge reopen-requested">Reopen requested</span>}
                      {log.reopen?.status === 'open' && <span className="status-badge reopened">Reopened</span>}
                      <span className="log-date">{formatDateTime(log.started_at)}</span>
                      {logIsInProgress && (
                        <button
                          type="button"
                          className="btn-continue-log"
                          onClick={() => {
                            setEditingWorkLog(log);
                            setShowWorkLogModal(true);
                          }}
                        >
                          Continue
                        </button>
                      )}
                      {canConfirmLog(log) && (
                        <button
                          type="button"
                          className="btn-continue-log"
                          onClick={() => setConfirmLog(log)}
                        >
                          Confirm my hours
                        </button>
                      )}
                      {canChangeCrew(log) && (
                        <button
                          type="button"
                          className="btn-log-action"
                          onClick={() => setCrewModalLog(log)}
                        >
                          Change crew
                        </button>
                      )}
                      {reopenOffers.update && (
                        <button
                          type="button"
                          className="btn-continue-log"
                          onClick={() => setReopenWindow({ kind: 'update', log })}
                        >
                          Update work log
                        </button>
                      )}
                      {reopenOffers.request && (
                        <button
                          type="button"
                          className="btn-log-action"
                          onClick={() => setReopenWindow({ kind: 'request', log })}
                        >
                          Request reopen
                        </button>
                      )}
                      {reopenOffers.cancel && (
                        <button
                          type="button"
                          className="btn-log-action"
                          onClick={() => cancelReopen(log)}
                        >
                          Cancel request
                        </button>
                      )}
                      {reopenOffers.decide && (
                        <>
                          <button
                            type="button"
                            className="btn-continue-log"
                            onClick={() => setReopenWindow({ kind: 'approve', log })}
                          >
                            Reopen
                          </button>
                          <button
                            type="button"
                            className="btn-log-action"
                            onClick={() => setReopenWindow({ kind: 'decline', log })}
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {reopenOffers.reopen && (
                        <button
                          type="button"
                          className="btn-log-action"
                          onClick={() => setReopenWindow({ kind: 'reopen', log })}
                        >
                          Reopen
                        </button>
                      )}
                      {canMove() && (
                        <button
                          type="button"
                          className="btn-log-action"
                          onClick={() => setMoveModalLog(log)}
                        >
                          Move
                        </button>
                      )}
                    </div>
                    <div className="log-body">
                      {/* Who else was on this job, and the three audited
                          actions that can change it (Gareth 2026-09-11, move
                          added 2026-09-15). Crew changes are keyed on the
                          LEAD's log being open; moving work to another ticket
                          is a manager action with a reason. Reopening a
                          completed log to update it is asked for by its
                          engineer and done by a team leader, manager or admin
                          (2026-10-13). */}
                      {crewFor(log).length > 1 && (
                        <div className="log-crew">
                          <span className="log-crew-label">On this job:</span>
                          {crewFor(log).map(member => (
                            <span key={member.log_id} className="log-crew-member">
                              {member.engineer_name}
                              {member.log_id === log.work_log_group_id ? ' (led)' : ''}
                              {member.outcome ? '' : ' — not signed off'}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="log-meta">
                        <span>By {log.engineer_name}</span>
                        {!logIsInProgress && log.duration_minutes && (
                          <span>Duration: {formatDuration(log.duration_minutes)}</span>
                        )}
                        <span className={`period-badge ${log.work_period}`}>{log.work_period.replace(/_/g, ' ')}</span>
                        {log.is_on_call_callout && <span className="oncall-badge">On-call</span>}
                      </div>
                      {log.notes && <p className="log-notes">{log.notes}</p>}
                      {log.outcome && (
                        <div className="log-outcome">
                          Outcome: {log.outcome.replace(/_/g, ' ')}
                        </div>
                      )}
                      {logParts && <div className="log-notes log-parts">Parts used on this log: {logParts}</div>}
                      {log.vehicle_check && (
                        <div className="vehicle-check-summary">
                          <div>
                            <span>Vehicle used</span>
                            <strong>{log.vehicle_check.vehicle_registration_number || '-'}</strong>
                          </div>
                          <div>
                            <span>Employees travelling</span>
                            <strong>
                              {log.vehicle_check.employees_present_names?.length
                                ? log.vehicle_check.employees_present_names.join(', ')
                                : '-'}
                            </strong>
                          </div>
                          <div>
                            <span>Daily Van Check</span>
                            <strong>DVC-{String(log.vehicle_check.check_id).slice(0, 8)}</strong>
                          </div>
                        </div>
                      )}
                      {/* Shown only until the forms list has it: the list
                          says the same, and opens the form. */}
                      {log.robot_check && !robotFormListed && (
                        <div className="robot-check-summary-card">
                          <div className="robot-check-status-line">
                            <span className="robot-check-label">Robot Check:</span>
                            <span className={`robot-check-status ${log.robot_check.check_data?.check_completed ? 'completed' : 'in-progress'}`}>
                              {log.robot_check.form_type === 'brush_robot_check' ? 'Brush' : 'Spray'} Robot Check {log.robot_check.check_data?.check_completed ? 'Completed' : 'In Progress'}
                            </span>
                          </div>
                          {log.robot_check.attachment_count > 0 && (
                            <div className="robot-check-photos-count">
                              <span>{log.robot_check.attachment_count} photo{log.robot_check.attachment_count !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <WorkLogFormList forms={formsOnLog} onOpen={form => setOpenFormId(form.check_id)} />
                      <AttachmentList attachments={log.attachments} compact />
                      <ReopenStatusLine reopen={log.reopen} />
                      {log.reopen && (
                        <WorkLogChanges basePath={logPath(log)} authFetch={authFetch} refreshKey={changesKey} />
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Parts used on this ticket (spare parts, Push 2). Engineers see
                no cost or charge (the API strips them); team leaders and up
                can correct or void a part. Parts are added in Log Work. */}
            <div className="ticket-parts-used">
              <PartsUsedList
                rows={partsUsed}
                robots={[]}
                showRobot={false}
                currentUser={user}
                authFetch={authFetch}
                onChanged={fetchTicket}
                status={partsStatus}
                onRetry={fetchPartsUsed}
                subject="ticket"
              />
            </div>
          </div>
        )}

        {activeTab === 'costs' && (
          <div className="costs-section">
            {costsLoading && <div className="loading">Computing costs...</div>}
            {!costsLoading && costs && (
              <>
                {costs.totals.absorbed_reason === 'service_contract' && (
                  <div className="costs-cover-banner">
                    Covered by the customer&apos;s service contract — costs are calculated in full but this work is not billable.
                  </div>
                )}
                {costs.totals.absorbed_reason === 'warranty' && (
                  <div className="costs-cover-banner">
                    Under warranty — costs are calculated in full but labour, call-outs and travel are not billable.
                  </div>
                )}
                <div className="costs-summary-cards">
                  <div className="cost-card">
                    <span className="cost-label">Internal Cost</span>
                    <span className="cost-value">{formatMoney(costs.totals.internal_cost_minor_units)}</span>
                  </div>
                  <div className="cost-card">
                    <span className="cost-label">Gross Charge</span>
                    <span className="cost-value">{formatMoney(costs.totals.gross_charge_minor_units)}</span>
                  </div>
                  <div className="cost-card highlight">
                    <span className="cost-label">Billable</span>
                    <span className="cost-value">{formatMoney(costs.totals.billable_charge_minor_units)}</span>
                  </div>
                  <div className="cost-card">
                    <span className="cost-label">
                      {costs.totals.absorbed_reason === 'service_contract' ? 'Contract Absorbed' : 'Warranty Absorbed'}
                    </span>
                    <span className="cost-value">{formatMoney(costs.totals.absorbed_minor_units ?? costs.totals.warranty_absorbed_minor_units)}</span>
                  </div>
                </div>

                <table className="costs-breakdown-table">
                  <thead>
                    <tr><th>Component</th><th>Detail</th><th>Cost</th><th>Charge</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Labour</td>
                      <td>{costs.labour.hours}h across {costs.labour.lines.length} work log(s)</td>
                      <td>{formatMoney(costs.labour.cost_minor_units)}</td>
                      <td>{formatMoney(costs.labour.charge_minor_units)}</td>
                    </tr>
                    <tr>
                      <td>Parts</td>
                      <td>{costs.parts.count} item(s){costs.parts.charge_warranty_minor_units > 0 ? ` (${formatMoney(costs.parts.charge_warranty_minor_units)} under warranty)` : ''}</td>
                      <td>{formatMoney(costs.parts.cost_minor_units)}</td>
                      <td>{formatMoney(costs.parts.charge_billable_minor_units)}</td>
                    </tr>
                    <tr>
                      <td>Travel</td>
                      <td>
                        {costs.travel.miles} miles{costs.travel.has_estimated_miles ? ' (est.)' : ''}
                        {costs.travel.other_expenses_minor_units > 0 ? ` + ${formatMoney(costs.travel.other_expenses_minor_units)} expenses` : ''}
                      </td>
                      <td>{formatMoney(costs.travel.mileage_cost_minor_units + costs.travel.other_expenses_minor_units)}</td>
                      {/* Mileage is cost-only for now (Gareth 2026-08-02) - the
                          billable rate exists in Rate Settings but is not charged */}
                      <td>{formatMoney(0)}</td>
                    </tr>
                    <tr>
                      <td>On-call incidents</td>
                      <td>{costs.callout.count} incident night(s)</td>
                      <td>{formatMoney(costs.callout.cost_minor_units ?? 0)}</td>
                      <td>{formatMoney(costs.callout.remote_fee_charge_minor_units ?? costs.callout.fee_minor_units)}</td>
                    </tr>
                  </tbody>
                </table>

                {costs.ticket.is_warranty && (
                  <div className="costs-warranty-note">
                    Warranty ticket — labour, call-out and travel charges are absorbed; only explicitly billable parts remain chargeable.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="review-notes-section">
            {/* Notes from the Jobs & Tickets board review (2026-10-20). */}
            <ReviewNotes type="ticket" id={id} />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="assignment-section">
            <div className="list-header">
              <h3>Assignment History</h3>
            </div>
            {!ticket.assignment_history || ticket.assignment_history.length === 0 ? (
              <div className="empty-message">No assignment history</div>
            ) : (
              <div className="assignment-list">
                {ticket.assignment_history.map(entry => (
                  <div key={entry.assignment_id} className="assignment-item">
                    <div className="assignment-header">
                      <span className="assignment-date">{formatDateTime(entry.assigned_at)}</span>
                      <span className="assignment-reason">{entry.reason.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="assignment-body">
                      <span>
                        {entry.assigned_from_name ? `${entry.assigned_from_name} → ` : ''}
                        <strong>{entry.assigned_to_name}</strong>
                      </span>
                      <span className="assigned-by">by {entry.assigned_by_name}</span>
                    </div>
                    {entry.handover_notes && (
                      <p className="handover-notes">{entry.handover_notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Work Log Modal */}
      {crewModalLog && (
        <CrewChangeModal
          ticketId={id}
          leadLog={crewModalLog}
          crew={crewFor(crewModalLog)}
          engineers={engineers}
          authFetch={authFetch}
          onClose={() => setCrewModalLog(null)}
          onSaved={() => { setCrewModalLog(null); fetchTicket(); }}
        />
      )}

      {reopenWindow?.kind === 'request' && (
        <RequestReopenModal
          basePath={logPath(reopenWindow.log)}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onDone={(reopen) => reopenStepDone(
            `Your request has been sent${reopen?.sent_to ? ` to ${reopen.sent_to}` : ''}. You will be told when the work log is reopened.`
          )}
        />
      )}

      {(reopenWindow?.kind === 'approve' || reopenWindow?.kind === 'decline') && (
        <DecideReopenModal
          basePath={logPath(reopenWindow.log)}
          log={reopenWindow.log}
          reopen={reopenWindow.log.reopen}
          mode={reopenWindow.kind}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onDone={() => reopenStepDone(reopenWindow.kind === 'approve'
            ? `The work log is reopened. ${reopenWindow.log.engineer_name || 'The engineer'} has been told.`
            : `The request was declined. ${reopenWindow.log.engineer_name || 'The engineer'} has been told why.`)}
        />
      )}

      {reopenWindow?.kind === 'reopen' && (
        <ReopenLogModal
          basePath={logPath(reopenWindow.log)}
          log={reopenWindow.log}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onDone={() => reopenStepDone(
            `The work log is reopened. ${reopenWindow.log.engineer_name || 'The engineer'} has been told.`
          )}
        />
      )}

      {reopenWindow?.kind === 'update' && (
        <UpdateWorkLogModal
          basePath={logPath(reopenWindow.log)}
          ticketId={id}
          log={reopenWindow.log}
          user={user}
          closedTheWork={outcomeClosesWork(workLogConfig, reopenWindow.log.outcome)}
          summary={{ resolution_summary: ticket.resolution_summary, root_cause: ticket.root_cause }}
          forms={logForms.filter(form => form.work_log_id === reopenWindow.log.log_id)}
          catalogue={user && canFitParts(user.role) ? spareParts : null}
          recordedParts={partsSummaryForLog(partsUsed, reopenWindow.log.log_id) || ''}
          authFetch={authFetch}
          onClose={() => setReopenWindow(null)}
          onSaved={(result) => {
            if (result.payrollNotice) setPayrollNotice(result.payrollNotice);
            const notAgain = result.alreadyRecorded.length > 0
              ? ` Already recorded, so not added again: ${result.alreadyRecorded.join(', ')}.`
              : '';
            reopenStepDone(`${result.completed ? COMPLETED_AGAIN_MESSAGE : SAVED_STILL_REOPENED_MESSAGE}${notAgain}`);
          }}
        />
      )}

      {confirmElement}

      {moveModalLog && (
        <MoveLogModal
          ticketId={id}
          ticketRef={ticket.ticket_ref}
          log={moveModalLog}
          customerId={ticket.customer_id}
          user={user}
          authFetch={authFetch}
          onClose={() => setMoveModalLog(null)}
          onSaved={(result) => {
            setMoveModalLog(null);
            setMoveMessage(result?.message || 'The work log was moved.');
            fetchTicket();
          }}
        />
      )}

      {/* Confirm my hours: a co-engineer's own crew entry (Phase 6 step 0f).
          The narrow hours route changes only this entry, never the ticket. */}
      {confirmLog && (
        <ConfirmHoursModal
          endpoint={`/api/v1/tickets/${id}/work-log/${confirmLog.log_id}/hours`}
          workLabel="ticket"
          log={confirmLog}
          leadName={leadNameFor(confirmLog)}
          authFetch={authFetch}
          onClose={() => setConfirmLog(null)}
          onSaved={(notice) => {
            setConfirmLog(null);
            fetchTicket();
            setPayrollNotice(notice || null);
          }}
        />
      )}

      {openFormId && (
        <WorkLogFormViewer checkId={openFormId} authFetch={authFetch} onClose={() => setOpenFormId(null)} linkToWork={false} />
      )}

      {showWorkLogModal && (
        <WorkLogModal
          ticketId={id}
          ticket={ticket}
          authFetch={authFetch}
          config={workLogConfig}
          engineers={engineers}
          workLog={editingWorkLog}
          spareParts={spareParts}
          recordedParts={partsUsed}
          recordedPartsStatus={partsStatus}
          onClose={() => {
            setShowWorkLogModal(false);
            setEditingWorkLog(null);
          }}
          onSaved={(payrollNotice) => {
            fetchTicket();
            setShowWorkLogModal(false);
            setEditingWorkLog(null);
            setPayrollNotice(payrollNotice || null);
          }}
        />
      )}

      {/* Link to Previous Ticket Modal */}
      {showLinkModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link to Previous Ticket</h2>
              <button className="modal-close" onClick={() => setShowLinkModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="link-picker-hint">
                {linkable.scope === 'asset'
                  ? 'Closed tickets for this asset — pick the one this fault follows on from.'
                  : 'Closed tickets for this customer — pick the one this fault follows on from.'}
              </p>
              {linkable.loading ? (
                <div className="loading">Loading previous tickets...</div>
              ) : linkable.tickets.length === 0 ? (
                <div className="empty-message">No closed tickets to link to.</div>
              ) : (
                <div className="link-picker-list">
                  {linkable.tickets.map(candidate => (
                    <button
                      key={candidate.ticket_id}
                      type="button"
                      className="link-picker-row"
                      disabled={linkSaving}
                      onClick={() => saveLink(candidate.ticket_id)}
                    >
                      <div className="link-picker-top">
                        <span className="chain-ref">{candidate.ticket_ref}</span>
                        <span className="link-picker-date">Closed {formatDate(candidate.closed_at || candidate.resolved_at)}</span>
                      </div>
                      <div className="link-picker-subject">{candidate.subject}</div>
                      {candidate.resolution_summary && (
                        <div className="link-picker-resolution">{candidate.resolution_summary}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {ticket.previous_ticket_id && (
                <button type="button" className="btn-cancel link-remove-btn" disabled={linkSaving} onClick={() => saveLink(null)}>
                  Remove Current Link
                </button>
              )}
              <button type="button" className="btn-cancel" onClick={() => setShowLinkModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Modal */}
      {showReopenModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reopen {ticket.ticket_ref}</h2>
              <button className="modal-close" onClick={() => setShowReopenModal(false)}>&times;</button>
            </div>
            <form onSubmit={submitReopen}>
              <div className="modal-body">
                {reopenError && <div className="modal-error">{reopenError}</div>}
                {ticket.closure_email_sent_at && (
                  <div className="alert-bar warning reopen-email-warning">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span className="alert-message">
                      The customer was emailed on {formatDate(ticket.closure_email_sent_at)} to say this was resolved.
                    </span>
                  </div>
                )}
                <p className="reopen-hint">
                  The recorded resolution moves to the customer timeline, the resolution SLA restarts
                  from now, and the ticket returns to In Progress.
                </p>
                <div className="form-group">
                  <label>Why is this being reopened? *</label>
                  <textarea
                    value={reopenForm.reason}
                    onChange={e => setReopenForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    required
                    placeholder="e.g. Customer called back — the fault has recurred"
                  />
                </div>
                <div className="form-group">
                  <label>Assign to</label>
                  <select
                    value={reopenForm.assigned_to_id}
                    onChange={e => setReopenForm(prev => ({ ...prev, assigned_to_id: e.target.value }))}
                  >
                    <option value="">
                      {ticket.assigned_to_name ? `${ticket.assigned_to_name} (previous assignee)` : 'Maintenance Planning Owner group'}
                    </option>
                    {engineers.map(engineer => (
                      <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowReopenModal(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={reopenSaving}>
                  {reopenSaving ? 'Reopening...' : 'Reopen Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Customer Modal */}
      {showEmailCustomerModal && (
        <EmailCustomerModal
          ticket={ticket}
          authFetch={authFetch}
          onClose={() => setShowEmailCustomerModal(false)}
          onSent={(updatedTicket) => {
            setShowEmailCustomerModal(false);
            setTicket(prev => ({ ...prev, ...updatedTicket }));
          }}
        />
      )}
    </div>
  );
}

function getPhotoUrl(att) {
  if (att.dataUrl) return att.dataUrl;
  if (att.url?.startsWith('/uploads/')) return authedFileHref(att.url);
  return att.url || '';
}

function PhotoThumb({ att, onRemove }) {
  const isSaved = Boolean(att.url && !att.dataUrl);
  const photoUrl = getPhotoUrl(att);

  return (
    <div className={`photo-thumb ${isSaved ? '' : 'unsaved'}`}>
      <a href={photoUrl} target="_blank" rel="noopener noreferrer" title={isSaved ? att.name : `${att.name} (pending upload)`}>
        <img src={photoUrl} alt={att.name} />
        {!isSaved && <span className="pending-badge">Pending</span>}
      </a>
      <button type="button" onClick={onRemove}>&times;</button>
    </div>
  );
}

function AttachmentList({ title, attachments = [], compact = false }) {
  const rows = Array.isArray(attachments) ? attachments : [];
  if (rows.length === 0) return null;

  const content = (
    <div className={compact ? 'attachment-list compact' : 'attachment-list'}>
      {rows.map((attachment, index) => {
        const href = authedFileHref(attachment.url);

        return attachment.url ? (
          <a key={attachment.attachment_id || index} href={href} target="_blank" rel="noreferrer" className="attachment-chip">
            {attachment.name || `Attachment ${index + 1}`}
          </a>
        ) : (
          <span key={attachment.attachment_id || index} className="attachment-chip muted">
            {attachment.name || `Attachment ${index + 1}`}
          </span>
        );
      })}
    </div>
  );

  if (compact) return content;
  return (
    <div className="detail-section full-width">
      <h3>{title || 'Attachments'}</h3>
      {content}
    </div>
  );
}

/**
 * Work Log Modal
 */
/**
 * Change who was on a job after the log was written (Gareth 2026-09-11).
 *
 * A reason is mandatory because this creates or destroys someone else's paid
 * hours, and each affected engineer is told what changed and why. The server
 * enforces all of it; this is the surface that makes it usable.
 */
function CrewChangeModal({ ticketId, leadLog, crew, engineers, authFetch, onClose, onSaved }) {
  const currentIds = crew.map(c => c.engineer_id).filter(id => id !== leadLog.engineer_id);
  const [selected, setSelected] = useState(currentIds);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const options = (engineers || []).filter(e => e.user_id && e.user_id !== leadLog.engineer_id);
  // Someone on the crew who has since left the engineers list still shows by name.
  const crewNames = Object.fromEntries(crew.map(c => [c.engineer_id, c.engineer_name]).filter(([, name]) => name));

  const add = selected.filter(id => !currentIds.includes(id));
  const remove = currentIds.filter(id => !selected.includes(id));
  const changed = add.length > 0 || remove.length > 0;
  // Mirrors the server's minimum so the button does not promise a save that 400s.
  const reasonOk = reason.trim().length >= 5;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/tickets/${ticketId}/work-log/${leadLog.log_id}/crew`, {
        method: 'POST',
        body: JSON.stringify({ add, remove, reason: reason.trim() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not change the crew');
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h2>Who was on this job</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="crew_engineer_picker">Engineers</label>
            <EngineerPicker
              id="crew_engineer_picker"
              options={options}
              selectedIds={selected}
              onChange={setSelected}
              knownNames={crewNames}
              emptyText={`Only ${leadLog.engineer_name || "the log's author"} is on this job.`}
            />
            <span className="field-help">
              {leadLog.engineer_name || 'The log\'s author'} led this job and cannot be removed.
              Adding someone creates a work log for them; removing someone deletes theirs, which is
              only possible while they have not signed it off.
            </span>
          </div>
          <div className="form-group">
            <label htmlFor="crew_reason">Why is this changing? *</label>
            <textarea
              id="crew_reason"
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Mike was on this job too but I forgot to add him"
            />
            <span className="field-help">
              Recorded against the work log, and sent to each engineer whose hours change.
            </span>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-save"
            onClick={submit}
            disabled={saving || !changed || !reasonOk}
          >
            {saving ? 'Saving...' : 'Save crew'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkLogModal({ ticketId, ticket, workLog, authFetch, config, engineers, spareParts = null, recordedParts = [], recordedPartsStatus = 'ready', onClose, onSaved }) {
  const { user: modalUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const isEditing = Boolean(workLog?.log_id);
  const defaultWorkType = config.work_types?.[0]?.value || 'remote_session';
  const defaultWorkPeriod = config.work_periods?.[0]?.value || 'in_hours';
  const isFirstLog = !isEditing && !(ticket?.work_logs?.length);
  const defaultStartedAt = isFirstLog && ticket?.reported_start_at ? toDateTimeInput(ticket.reported_start_at) : null;
  const [formData, setFormData] = useState(() => initialWorkLogForm(workLog, defaultWorkType, defaultWorkPeriod, defaultStartedAt));

  // Parts used (spare parts, Push 2): searched in the phone's copy of the
  // list and sent INSIDE the work log request, so the server records them in
  // the same transaction as the work. A part it refuses saves nothing at all,
  // and the lines stay here with the reason. No prices: the server prices
  // each part, and engineers never see a cost or a charge. Each line is
  // { key, value }; the key is its line id for life.
  //
  // Only roles that record parts (canFitParts, the mirror of the API's
  // PARTS_FIT) get the section: the server refuses a work log that carries
  // parts from any other role, saving nothing. Such a role is shown no
  // section and no lines kept on this phone are read back for it, so a save
  // can never carry a part its user cannot see or remove.
  const canRecordParts = canFitParts(modalUser?.role);
  const sparesKey = ticketSparesKey(modalUser?.userId, ticketId, workLog?.log_id);
  const [restoredSpares] = useState(() => (canRecordParts ? readTicketSpares(sparesKey) : []));
  const [spareLines, setSpareLines] = useState(restoredSpares);
  // Read by Save, so a quantity committed as the Save button takes the focus is sent.
  const spareLinesRef = useRef(spareLines);
  // The sentence for each line whose quantity box holds text that cannot be
  // read, so Save stops on it instead of sending the old quantity.
  const spareTextProblemsRef = useRef({});
  const modalBodyRef = useRef(null);

  // Every change is kept on the phone until the work log saves or the window closes.
  const updateSpareLines = (change) => {
    const next = change(spareLinesRef.current);
    spareLinesRef.current = next;
    setSpareLines(next);
    writeTicketSpares(sparesKey, next);
  };

  const addSpareLine = (picked) => updateSpareLines(lines => [...lines, {
    key: newUuid(),
    value: {
      catalogue_id: picked.notInList ? null : picked.id,
      item_number: picked.notInList ? null : (picked.number || null),
      name: picked.name,
      quantity: 1,
      asset_id: null,
      whole_job: false,
      not_in_list: Boolean(picked.notInList),
      added_by: modalUser?.userId || null,
      added_at: new Date().toISOString()
    }
  }]);
  const changeSpareLine = (key, value) => updateSpareLines(lines => lines.map(line => (line.key === key ? { ...line, value } : line)));
  const removeSpareLine = (key) => updateSpareLines(lines => lines.filter(line => line.key !== key));

  // Continuing a log: the parts already recorded on it. They are filed on the
  // crew's lead log, so a crew member's own entry shows the lead's.
  const partsLogId = String((workLog && (workLog.work_log_group_id || workLog.log_id)) || '').toLowerCase();
  const alreadyRecorded = isEditing
    ? (recordedParts || []).filter(part => part && !part.voided_at && String(part.work_log_id || '').toLowerCase() === partsLogId)
    : [];

  // Closing the window forgets the lines it held: they were never saved.
  const closeWindow = () => {
    forgetTicketSpares(sparesKey);
    onClose();
  };

  // A refusal shows at the top of a long form, and Save was tapped at the
  // bottom: bring it into view.
  useEffect(() => {
    if (error && modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  }, [error]);

  // Robot-check template ambiguity message for a /resolve 409 outside the
  // site_work fallback chain (shared by the Brush and Spray slots).
  const robotCheckConflictMessage = (errorData) => {
    const conflictNames = errorData.conflicting_templates?.map(t => t.name).join(', ') || 'multiple templates';
    return `Targeting ambiguity: multiple templates match (${conflictNames}). Contact admin to fix template targeting.`;
  };

  // Restores a saved brush/spray robot check snapshot from the work log being
  // edited (template + metadata + answers + 'template:' step attachments).
  const savedRobotCheckLoader = (formType) => () => {
    const savedRobotCheck = workLog?.robot_check;
    const checkData = savedRobotCheck?.check_data || {};
    if (savedRobotCheck?.form_type !== formType || !checkData.template_snapshot) {
      return null;
    }
    const snapshot = checkData.template_snapshot;
    const attachments = savedRobotCheck.attachments || [];
    const templateStepAttachments = {};
    attachments.forEach(att => {
      if (att.step_key && att.step_key.startsWith('template:')) {
        if (!templateStepAttachments[att.step_key]) {
          templateStepAttachments[att.step_key] = [];
        }
        templateStepAttachments[att.step_key].push(att);
      }
    });
    return {
      template: {
        template_id: checkData.template_id,
        version: checkData.template_version,
        schema_json: snapshot
      },
      metadata: {
        template_id: checkData.template_id,
        template_version: checkData.template_version,
        template_snapshot: snapshot
      },
      answers: checkData.template_answers || {},
      attachments: templateStepAttachments
    };
  };

  // Brush Robot Check template slot
  const brushCheck = useTemplateCheckForm({
    authFetch,
    enabled: Boolean(ticket?.asset_id && isBrushRobotType(ticket?.robot_type)),
    resolveParams: {
      category: 'robot_check',
      workLogType: formData.log_type,
      assetType: ticket?.robot_type
    },
    loadSaved: savedRobotCheckLoader('brush_robot_check'),
    skipSavedAfterParamsChange: true,
    fallback: { formType: 'brush_robot_check' },
    conflictMessage: robotCheckConflictMessage,
    fallbackConflictMessage: (errorData) => {
      const conflictNames = errorData.conflicting_templates?.map(t => `"${t.name}" (${t.target_work_log_types?.join(', ') || 'all work types'})`).join(', ') || 'multiple templates';
      return `Multiple active Brush Robot Check templates exist: ${conflictNames}. An admin needs to configure targeting in Settings > Check Forms, or use /resolve with targeting criteria. Using hard-coded fallback form.`;
    },
    onError: setError,
    onParamsChangeReset: () => {
      setFormData(prev => ({
        ...prev,
        brush_robot_check: { ...prev.brush_robot_check, check_completed: false }
      }));
    },
    logLabel: 'Brush Robot Check'
  });
  const {
    template: brushTemplate,
    loading: brushTemplateLoading,
    failed: brushTemplateFailed,
    answers: brushTemplateAnswers,
    setAnswers: setBrushTemplateAnswers,
    metadata: brushTemplateMetadata,
    attachments: brushTemplateAttachments,
    addAttachments: addBrushTemplateAttachments,
    removeAttachment: removeBrushTemplateAttachment,
    resolveWarning: brushResolveWarning
  } = brushCheck;

  // Spray Robot Check template slot
  const sprayCheck = useTemplateCheckForm({
    authFetch,
    enabled: Boolean(ticket?.asset_id && isSprayRobotType(ticket?.robot_type)),
    resolveParams: {
      category: 'robot_check',
      workLogType: formData.log_type,
      assetType: ticket?.robot_type
    },
    loadSaved: savedRobotCheckLoader('spray_robot_check'),
    skipSavedAfterParamsChange: true,
    fallback: { formType: 'spray_robot_check' },
    conflictMessage: robotCheckConflictMessage,
    fallbackConflictMessage: (errorData) => {
      const conflictNames = errorData.conflicting_templates?.map(t => `"${t.name}" (${t.target_work_log_types?.join(', ') || 'all work types'})`).join(', ') || 'multiple templates';
      return `Multiple active Spray Robot Check templates exist: ${conflictNames}. An admin needs to configure targeting in Settings > Check Forms, or use /resolve with targeting criteria. Using hard-coded fallback form.`;
    },
    onError: setError,
    onParamsChangeReset: () => {
      setFormData(prev => ({
        ...prev,
        spray_robot_check: { ...prev.spray_robot_check, check_completed: false }
      }));
    },
    logLabel: 'Spray Robot Check'
  });
  const {
    template: sprayTemplate,
    loading: sprayTemplateLoading,
    failed: sprayTemplateFailed,
    answers: sprayTemplateAnswers,
    setAnswers: setSprayTemplateAnswers,
    metadata: sprayTemplateMetadata,
    attachments: sprayTemplateAttachments,
    addAttachments: addSprayTemplateAttachments,
    removeAttachment: removeSprayTemplateAttachment,
    resolveWarning: sprayResolveWarning
  } = sprayCheck;

  // Generic template check slot (for non-Brush/non-Spray assets)
  // Generic check availability is driven by template targeting via /resolve, not by a
  // hard-coded work-type list. Any work log type can have a generic check if an admin
  // has configured a template targeting that work type and asset type.
  const genericCheck = useTemplateCheckForm({
    authFetch,
    enabled: Boolean(ticket?.asset_id && isGenericAssetType(ticket?.robot_type)),
    resolveParams: {
      category: 'generic_check',
      workLogType: formData.log_type,
      assetType: ticket?.robot_type
    },
    // If editing an existing work log, restore any saved generic check first
    // (snapshot protection - the saved template_snapshot is used for rendering).
    loadSaved: async () => {
      if (!workLog?.log_id) return null;
      try {
        console.log('[Generic Check] Fetching saved template checks for work log:', workLog.log_id);
        const savedChecksResponse = await authFetch(`/api/v1/work-logs/${workLog.log_id}/template-checks`);
        if (savedChecksResponse.ok) {
          const savedChecksData = await savedChecksResponse.json();
          const savedChecks = savedChecksData.data || [];
          const savedGenericCheck = savedChecks.find(
            check => check.check_category === 'generic_check'
          );
          if (savedGenericCheck) {
            console.log('[Generic Check] Found saved check, using snapshot:', savedGenericCheck.template_key);
            const attachments = savedGenericCheck.attachments || [];
            const stepAttachments = {};
            attachments.forEach(att => {
              if (att.step_key && att.step_key.startsWith('template:')) {
                if (!stepAttachments[att.step_key]) {
                  stepAttachments[att.step_key] = [];
                }
                stepAttachments[att.step_key].push(att);
              }
            });
            return {
              template: {
                template_id: savedGenericCheck.template_id,
                template_key: savedGenericCheck.template_key,
                version: savedGenericCheck.template_version,
                name: savedGenericCheck.template_snapshot?.name || savedGenericCheck.form_type,
                form_type: savedGenericCheck.form_type,
                check_category: savedGenericCheck.check_category,
                schema_json: savedGenericCheck.template_snapshot
              },
              answers: savedGenericCheck.template_answers || {},
              attachments: stepAttachments,
              completed: savedGenericCheck.check_completed === true,
              savedCheck: savedGenericCheck
            };
          }
        }
      } catch (err) {
        console.error('[Generic Check] Error fetching saved template checks:', err);
        // Continue to /resolve as fallback
      }
      return null;
    },
    resetOnDisabled: true,
    conflictMessage: (errorData) => {
      const conflictNames = errorData.conflicting_templates?.map(t => t.name).join(', ') || 'multiple templates';
      return `Multiple templates match this asset type (${conflictNames}). An admin needs to configure targeting in Settings > Check Forms.`;
    },
    onError: setError,
    logLabel: 'Generic Check'
  });
  const {
    template: genericTemplate,
    loading: genericTemplateLoading,
    failed: genericTemplateFailed,
    answers: genericTemplateAnswers,
    setAnswers: setGenericTemplateAnswers,
    attachments: genericTemplateAttachments,
    addAttachments: addGenericTemplateAttachments,
    removeAttachment: removeGenericTemplateAttachment,
    resolveWarning: genericResolveWarning,
    completed: genericCheckCompleted,
    setCompleted: setGenericCheckCompleted,
    savedCheck: savedGenericTemplateCheck
  } = genericCheck;

  // Job Stages (site work): parent holds the travel record so its id can be
  // linked into the work-log payload on save; JobStagesCard does the rest.
  const [travelRecord, setTravelRecord] = useState(null);

  const selectedOutcome = config.outcomes?.find(outcome => outcome.value === formData.outcome);
  const hasOutcome = formData.outcome !== '';
  // Codes already recorded across this ticket's logs (the API rolls them up),
  // shown read-only so the next engineer gets the context without inheriting
  // someone else's findings into their own log. When EDITING, this log's own
  // codes are in the box, so the context is the other logs' codes — otherwise
  // the log's own code appears twice and reads as someone else's finding.
  const priorFaultCodes = workLog?.log_id && Array.isArray(ticket?.work_logs)
    ? faultCodesFromOtherLogs(ticket.work_logs, workLog.log_id)
    : parseFaultCodes(ticket?.fault_codes);
  // Live echo of what THIS log will store — how the comma convention explains
  // itself on a phone in a parlour.
  const parsedFaultCodes = parseFaultCodes(formData.fault_codes);
  // Everyone except the person writing the log — they are the lead, and adding
  // yourself would double your own hours on the ticket.
  const coEngineerOptions = (engineers || []).filter(e => e.user_id && e.user_id !== modalUser?.userId);
  // The configured close/handover flag decides; the legacy list only covers
  // outcomes the config doesn't know (matches utils/workOutcomes.js API-side).
  const closesWork = Boolean(selectedOutcome
    ? selectedOutcome.closes_work
    : ['resolved', 'completed', 'complete'].includes(formData.outcome));
  const isSiteWork = formData.log_type === 'site_work';
  const isInProgress = !hasOutcome;

  const stageStampTime = (value) => value
    ? new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  // "Remote" label used only for Remote Session / Diagnosis copy in the UI. It no longer
  // gates whether a robot check is attempted - that is now decided entirely by asset type
  // (Brush/Spray) plus Check Form Template targeting for the selected work log type.
  const isRemoteWork = formData.log_type === 'remote_session' || formData.log_type === 'diagnosis';


  // Brush/Spray path is chosen by asset type alone. Whether a check actually applies for the
  // selected work log type is decided by /check-form-templates/resolve targeting, not by a
  // hard-coded work log type list, so custom Scheduled & Ticketed Support work log types
  // (e.g. "installation", "commissioning") can trigger a targeted Brush/Spray template.
  const requiresBrushRobotCheck = ticket?.asset_id && isBrushRobotType(ticket?.robot_type);
  const requiresSprayRobotCheck = ticket?.asset_id && isSprayRobotType(ticket?.robot_type);

  // Determine if we should use the template-driven renderer or fallback to hard-coded
  // Fallback (the legacy hard-coded form) is only used for Site Work - see useTemplateCheckForm.
  const useBrushTemplate = requiresBrushRobotCheck && brushTemplate && !brushTemplateLoading && !brushTemplateFailed;
  const useBrushFallback = requiresBrushRobotCheck && isSiteWork && (brushTemplateFailed || (!brushTemplate && !brushTemplateLoading));

  // Spray Robot Check template flags
  // Fallback (the legacy hard-coded form) is only used for Site Work - see useTemplateCheckForm.
  const useSprayTemplate = requiresSprayRobotCheck && sprayTemplate && !sprayTemplateLoading && !sprayTemplateFailed;
  const useSprayFallback = requiresSprayRobotCheck && isSiteWork && (sprayTemplateFailed || (!sprayTemplate && !sprayTemplateLoading));

  // Generic template check flags (for non-Brush/non-Spray assets)
  // Generic check availability is driven by template targeting via /resolve.
  // If the asset is non-Brush/non-Spray and has an ID, we call /resolve to see
  // if any template targets this work type + asset type combination.
  // No fallback for generic checks - if no template found (404), show nothing.
  const canHaveGenericCheck = ticket?.asset_id && isGenericAssetType(ticket?.robot_type);
  const useGenericTemplate = canHaveGenericCheck && genericTemplate && !genericTemplateLoading && !genericTemplateFailed;
  const showGenericWarning = canHaveGenericCheck && genericResolveWarning && !genericTemplate && !genericTemplateLoading;

  // A template whose authored content carries the system 'check_completed'
  // field (the Completion section built in Settings > Check Forms) OWNS the
  // completion tick: the box lives inside the rendered check form, and the
  // modal must not add a second mandatory one (team report 2026-08-02 - two
  // "Robot Check Complete" boxes on robot site work). The modal's own
  // confirm checkbox remains only for the legacy fallback form and for
  // templates without the field.
  const templateOwnsCompletion = (template, metadata) => formOwnsCompletion(metadata?.template_snapshot || template?.schema_json);

  // The robot check's name on screen is the form's own name when a form is in
  // use (Gareth 2026-10-22: a remote variant showed "Pre Robot Check
  // Complete"); the old fixed fallback form keeps its own wording.
  const brushFormName = useBrushTemplate && brushTemplate?.name
    ? brushTemplate.name
    : (isRemoteWork ? 'Remote Brush Diagnostic' : 'Brush Robot Check');
  const sprayFormName = useSprayTemplate && sprayTemplate?.name
    ? sprayTemplate.name
    : (isRemoteWork ? 'Remote Spray Diagnostic' : 'Spray Robot Check');
  const brushCompletionInTemplate = useBrushTemplate && templateOwnsCompletion(brushTemplate, brushTemplateMetadata);
  const sprayCompletionInTemplate = useSprayTemplate && templateOwnsCompletion(sprayTemplate, sprayTemplateMetadata);
  const genericCompletionInTemplate = useGenericTemplate && templateOwnsCompletion(genericTemplate, null);

  // Mirror the template's completion tick into the marker every downstream
  // consumer reads (validation, payload, status chips) - one source of truth.
  useEffect(() => {
    if (!brushCompletionInTemplate) return;
    const completed = completionTicked(brushTemplateAnswers);
    setFormData(prev => (prev.brush_robot_check.check_completed === completed
      ? prev
      : { ...prev, brush_robot_check: { ...prev.brush_robot_check, check_completed: completed } }));
  }, [brushCompletionInTemplate, brushTemplateAnswers]);

  useEffect(() => {
    if (!sprayCompletionInTemplate) return;
    const completed = completionTicked(sprayTemplateAnswers);
    setFormData(prev => (prev.spray_robot_check.check_completed === completed
      ? prev
      : { ...prev, spray_robot_check: { ...prev.spray_robot_check, check_completed: completed } }));
  }, [sprayCompletionInTemplate, sprayTemplateAnswers]);

  useEffect(() => {
    if (!genericCompletionInTemplate) return;
    const completed = completionTicked(genericTemplateAnswers);
    setGenericCheckCompleted(completed);
  }, [genericCompletionInTemplate, genericTemplateAnswers, setGenericCheckCompleted]);

  useEffect(() => {
    const fetchVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const response = await authFetch('/api/v1/tickets/active-vehicles');
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to load vehicles');
        let rows = data.data || [];
        if (workLog?.vehicle_check?.vehicle_id && !rows.some(vehicle => vehicle.vehicle_id === workLog.vehicle_check.vehicle_id)) {
          rows = [
            {
              vehicle_id: workLog.vehicle_check.vehicle_id,
              registration_number: workLog.vehicle_check.vehicle_registration_number || 'Previously selected vehicle',
              number_of_seats: workLog.vehicle_check.number_of_seats || Math.max(1, workLog.vehicle_check.employees_present_names?.length || 1),
              current_odometer_reading: workLog.vehicle_check.odometer_reading
            },
            ...rows
          ];
        }
        setVehicles(rows);
      } catch (err) {
        setError(err.message);
      } finally {
        setVehiclesLoading(false);
      }
    };

    fetchVehicles();
  }, [authFetch, workLog]);

  const updateDailyVanCheck = (field, value) => {
    setFormData(prev => ({
      ...prev,
      daily_van_check: {
        ...prev.daily_van_check,
        [field]: value
      }
    }));
  };

  const updateBrushRobotCheck = (field, value) => {
    setFormData(prev => ({
      ...prev,
      brush_robot_check: {
        ...prev.brush_robot_check,
        [field]: value
      }
    }));
  };

  const addBrushRobotCheckStepAttachments = async (stepKey, files, inputElement) => {
    try {
      const newAttachments = await filesToAttachmentPayload(files);
      const attachmentsWithStep = newAttachments.map(att => ({ ...att, step_key: stepKey }));
      setFormData(prev => ({
        ...prev,
        brush_robot_check: {
          ...prev.brush_robot_check,
          step_attachments: {
            ...prev.brush_robot_check.step_attachments,
            [stepKey]: [
              ...(prev.brush_robot_check.step_attachments[stepKey] || []),
              ...attachmentsWithStep
            ]
          }
        }
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      if (inputElement) inputElement.value = '';
    }
  };

  const removeBrushRobotCheckStepAttachment = (stepKey, index) => {
    setFormData(prev => {
      const currentAttachments = prev.brush_robot_check.step_attachments[stepKey] || [];
      const updatedAttachments = currentAttachments.filter((_, i) => i !== index);
      return {
        ...prev,
        brush_robot_check: {
          ...prev.brush_robot_check,
          step_attachments: {
            ...prev.brush_robot_check.step_attachments,
            [stepKey]: updatedAttachments
          }
        }
      };
    });
  };

  const updateSprayRobotCheck = (field, value) => {
    setFormData(prev => ({
      ...prev,
      spray_robot_check: {
        ...prev.spray_robot_check,
        [field]: value
      }
    }));
  };

  const addSprayRobotCheckStepAttachments = async (stepKey, files, inputElement) => {
    try {
      const newAttachments = await filesToAttachmentPayload(files);
      const attachmentsWithStep = newAttachments.map(att => ({ ...att, step_key: stepKey }));
      setFormData(prev => ({
        ...prev,
        spray_robot_check: {
          ...prev.spray_robot_check,
          step_attachments: {
            ...prev.spray_robot_check.step_attachments,
            [stepKey]: [
              ...(prev.spray_robot_check.step_attachments[stepKey] || []),
              ...attachmentsWithStep
            ]
          }
        }
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      if (inputElement) inputElement.value = '';
    }
  };

  const removeSprayRobotCheckStepAttachment = (stepKey, index) => {
    setFormData(prev => {
      const currentAttachments = prev.spray_robot_check.step_attachments[stepKey] || [];
      const updatedAttachments = currentAttachments.filter((_, i) => i !== index);
      return {
        ...prev,
        spray_robot_check: {
          ...prev.spray_robot_check,
          step_attachments: {
            ...prev.spray_robot_check.step_attachments,
            [stepKey]: updatedAttachments
          }
        }
      };
    });
  };

  // Van seats follow the CREW (Gareth 2026-09-11): the people on this job are
  // the only ones who can be recorded in its van. Before this the seat
  // dropdowns offered every engineer in the company.
  const seatCountOf = (v) => Number(v?.number_of_seats) || 0;
  const vanCrew = [
    ...(engineers || []).filter(e => e.user_id === modalUser?.userId),
    ...(engineers || []).filter(e => formData.additional_engineer_ids?.includes(e.user_id))
  ];
  const selectedVehicle = vehicles.find(v => v.vehicle_id === formData.daily_van_check.vehicle_id);
  const needsSecondVan = Boolean(selectedVehicle) && seatCountOf(selectedVehicle) > 0 && vanCrew.length > seatCountOf(selectedVehicle);
  const seatCount = selectedVehicle?.number_of_seats || 0;

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    const newSeatCount = vehicle?.number_of_seats || 0;
    const currentSeats = formData.daily_van_check.seat_selections || [];
    const trimmedSeats = currentSeats.slice(0, newSeatCount);

    setFormData(prev => ({
      ...prev,
      daily_van_check: {
        ...prev.daily_van_check,
        vehicle_id: vehicleId,
        seat_selections: trimmedSeats
      }
    }));
  };

  const updateSeatSelection = (seatIndex, userId) => {
    setFormData(prev => {
      const currentSeats = [...(prev.daily_van_check.seat_selections || [])];
      while (currentSeats.length <= seatIndex) {
        currentSeats.push('');
      }
      currentSeats[seatIndex] = userId;
      return {
        ...prev,
        daily_van_check: {
          ...prev.daily_van_check,
          seat_selections: currentSeats
        }
      };
    });
  };

  const getSelectedEmployeeIds = () => {
    return (formData.daily_van_check.seat_selections || []).filter(id => id && id !== '');
  };

  const isEmployeeAlreadySelected = (userId, currentSeatIndex) => {
    const seats = formData.daily_van_check.seat_selections || [];
    return seats.some((id, idx) => id === userId && idx !== currentSeatIndex);
  };

  // Damage photos APPEND — a phone camera delivers one photo per picker
  // visit, so replacing the array would cap the check at a single photo.
  const handleDamagePhotoChange = async (files, inputEl) => {
    try {
      const attachments = await filesToAttachmentPayload(files);
      setFormData(prev => ({
        ...prev,
        daily_van_check: {
          ...prev.daily_van_check,
          damage_photo_attachments: [
            ...(prev.daily_van_check.damage_photo_attachments || []),
            ...attachments
          ]
        }
      }));
      if (inputEl) inputEl.value = '';
    } catch (err) {
      setError(err.message);
    }
  };

  const removeDamagePhoto = (removeIdx) => {
    setFormData(prev => ({
      ...prev,
      daily_van_check: {
        ...prev.daily_van_check,
        damage_photo_attachments: (prev.daily_van_check.damage_photo_attachments || [])
          .filter((_, idx) => idx !== removeIdx)
      }
    }));
  };

  const handleChange = async (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === 'file') {
      const picker = e.target;
      try {
        const attachments = await filesToAttachmentPayload(files);
        setFormData(prev => ({ ...prev, attachments }));
      } catch (err) {
        // Refused files are not attached: the picker is emptied so it cannot go on
        // showing files that will not be saved.
        picker.value = '';
        setFormData(prev => ({ ...prev, attachments: savedAttachmentsOnly(prev.attachments) }));
        setError(err.message);
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'assign_next_type' && value !== 'user' ? { assigned_to_next_id: '' } : {})
    }));
  };

  const buildDailyVanCheckPayload = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    return {
      ...formData.daily_van_check,
      employees_present_user_ids: selectedEmployeeIds,
      current_odometer_reading: formData.daily_van_check.current_odometer_reading === ''
        ? ''
        : Number(formData.daily_van_check.current_odometer_reading),
      ...Object.fromEntries(
        DAILY_VAN_CHECK_BOOLEAN_FIELDS.map(([field]) => [
          field,
          formData.daily_van_check[field] === '' ? '' : formData.daily_van_check[field] === 'true'
        ])
      )
    };
  };

  // "Finish at home" (2026-08-21): everything is filled in, but the drive home
  // is not done, and ended_at should be door-to-door. Validates exactly as a
  // close, then parks the closing payload instead of applying it — the Today
  // screen replays it against the Arrived home stamp.
  const handleSubmit = async (e, { parkForHome = false } = {}) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Parts used first. A quantity box holding text that cannot be read
      // must never send the old quantity silently (only lines still shown
      // count); then the line checks, in the server's own words.
      const spareLinesToSend = spareLinesRef.current;
      const shownSpareKeys = new Set(spareLinesToSend.map(line => line.key));
      const spareTextProblem = Object.entries(spareTextProblemsRef.current)
        .filter(([key, sentence]) => sentence && shownSpareKeys.has(key))
        .map(([, sentence]) => sentence)[0];
      const spareProblem = spareTextProblem
        || firstSpareProblem(spareLinesToSend.map(line => line.value), { robotIds: new Set(), severalRobots: false });
      if (spareProblem) {
        setError(spareProblem);
        setSaving(false);
        return;
      }

      if (isSiteWork && getSelectedEmployeeIds().length === 0) {
        setError('At least one employee must be selected');
        setSaving(false);
        return;
      }

      const selectedIds = getSelectedEmployeeIds();
      const uniqueIds = new Set(selectedIds);
      if (selectedIds.length !== uniqueIds.size) {
        setError('The same employee cannot be selected for multiple seats');
        setSaving(false);
        return;
      }

      if (
        isSiteWork &&
        formData.daily_van_check.damage === 'true' &&
        formData.daily_van_check.damage_photo_attachments.length === 0
      ) {
        setError('Damage photo is required when vehicle damage is reported');
        setSaving(false);
        return;
      }

      // A handover completes this log and passes the baton — the notes ARE
      // the handover, so they are compulsory (Gareth 2026-08-12).
      if (hasOutcome && !closesWork && !formData.notes.trim()) {
        setError('Handover notes are required — write what the next owner needs to know in the Notes box.');
        setSaving(false);
        return;
      }

      // Every Job Stages time is needed before a site log completes: a close
      // and a hand over alike (Gareth 2026-10-07 and 2026-10-12). Finish at
      // home needs the first three. Said here in the server's own words,
      // before anything is sent. With no record on screen the server answers
      // instead: it may hold times this window has not been shown.
      if (isSiteWork && hasOutcome && travelRecord) {
        const stagesProblem = jobStagesProblem(travelRecord, { waitingForLast: parkForHome });
        if (stagesProblem) {
          setError(stagesProblem);
          setSaving(false);
          return;
        }
      }

      // Brush Robot Check completion validation
      // Only validate if a template or fallback is actually loaded (not for remote 404)
      if (closesWork && useBrushTemplate) {
        // Template-based validation using saved snapshot or active template schema
        const schemaToValidate = brushTemplateMetadata?.template_snapshot || brushTemplate?.schema_json;
        if (schemaToValidate) {
          const validation = validateAnswers(schemaToValidate, brushTemplateAnswers, { requireComplete: true, attachments: brushTemplateAttachments });
          if (!validation.valid) {
            const firstError = validation.errors[0];
            const errorMessage = firstError.section
              ? `${firstError.section}: ${firstError.message}`
              : firstError.message;
            setError(errorMessage);
            setSaving(false);
            return;
          }
        }
        // Also require the check_completed marker for status/summary compatibility
        if (!formData.brush_robot_check.check_completed) {
          const checkName = brushFormName;
          setError(`${checkName} must be marked as completed before finishing this work log`);
          setSaving(false);
          return;
        }
      } else if (closesWork && useBrushFallback) {
        // Fallback hard-coded validation (Site Work only)
        if (!formData.brush_robot_check.check_completed) {
          setError('Brush Robot Check must be completed before finishing site work');
          setSaving(false);
          return;
        }
        if (
          formData.brush_robot_check.cell_guarding_secure === 'false' &&
          !formData.brush_robot_check.cell_issues?.trim()
        ) {
          setError('Please describe the issues with the robot cell guarding');
          setSaving(false);
          return;
        }
      }
      // Non-site-work 409: brushResolveWarning is set but no template/fallback loaded
      // Block completion with clear message about targeting issue
      if (formData.outcome && !isSiteWork && requiresBrushRobotCheck && brushResolveWarning && !useBrushTemplate && !brushTemplateLoading) {
        setError('Cannot complete: Brush Robot Check template targeting needs to be fixed by an admin');
        setSaving(false);
        return;
      }

      // Spray Robot Check completion validation
      // Only validate if a template or fallback is actually loaded (not for remote 404)
      if (closesWork && useSprayTemplate) {
        // Template-based validation using saved snapshot or active template schema
        const schemaToValidate = sprayTemplateMetadata?.template_snapshot || sprayTemplate?.schema_json;
        if (schemaToValidate) {
          const validation = validateAnswers(schemaToValidate, sprayTemplateAnswers, { requireComplete: true, attachments: sprayTemplateAttachments });
          if (!validation.valid) {
            const firstError = validation.errors[0];
            const errorMessage = firstError.section
              ? `${firstError.section}: ${firstError.message}`
              : firstError.message;
            setError(errorMessage);
            setSaving(false);
            return;
          }
        }
        // Also require the check_completed marker for status/summary compatibility
        if (!formData.spray_robot_check.check_completed) {
          const checkName = sprayFormName;
          setError(`${checkName} must be marked as completed before finishing this work log`);
          setSaving(false);
          return;
        }
      } else if (closesWork && useSprayFallback) {
        // Fallback hard-coded validation (Site Work only)
        if (!formData.spray_robot_check.check_completed) {
          setError('Spray Robot Check must be completed before finishing site work');
          setSaving(false);
          return;
        }
        if (
          formData.spray_robot_check.cell_guarding_secure === 'false' &&
          !formData.spray_robot_check.cell_issues?.trim()
        ) {
          setError('Please describe the issues with the robot cell guarding');
          setSaving(false);
          return;
        }
      }
      // Non-site-work 409: sprayResolveWarning is set but no template/fallback loaded
      // Block completion with clear message about targeting issue
      if (formData.outcome && !isSiteWork && requiresSprayRobotCheck && sprayResolveWarning && !useSprayTemplate && !sprayTemplateLoading) {
        setError('Cannot complete: Spray Robot Check template targeting needs to be fixed by an admin');
        setSaving(false);
        return;
      }

      // Generic template check completion validation
      // Only validate when completing the work log (outcome is set), not for Save Progress
      if (closesWork && useGenericTemplate) {
        // Get the correct schema source: saved snapshot for reopened checks, active template for new checks
        const schemaToValidate = savedGenericTemplateCheck?.template_snapshot || genericTemplate?.schema_json;
        if (schemaToValidate) {
          const validation = validateAnswers(schemaToValidate, genericTemplateAnswers, { requireComplete: true, attachments: genericTemplateAttachments });
          if (!validation.valid) {
            const firstError = validation.errors[0];
            const checkName = genericTemplate?.name || 'Asset Check';
            const errorMessage = firstError.section
              ? `${checkName}: ${firstError.section}: ${firstError.message}`
              : `${checkName}: ${firstError.message}`;
            setError(errorMessage);
            setSaving(false);
            return;
          }
        }
        // Also require the check_completed marker
        if (!genericCheckCompleted) {
          const checkName = genericTemplate?.name || 'Asset Check';
          setError(`${checkName} must be marked as completed before finishing this work log`);
          setSaving(false);
          return;
        }
      }
      // Generic 409: genericResolveWarning is set but no template loaded
      // Block completion with clear message about targeting issue
      if (formData.outcome && showGenericWarning) {
        setError('Cannot complete: Asset Check template targeting needs to be fixed by an admin. Contact your administrator to configure targeting in Settings > Check Forms.');
        setSaving(false);
        return;
      }

      const buildBrushRobotCheckPayload = () => {
        // If using template renderer, include template metadata and answers in check_data
        if (useBrushTemplate && brushTemplateMetadata) {
          return {
            checkData: brushCheck.buildCheckData({
              check_completed: formData.brush_robot_check.check_completed
            }),
            // Template photo attachments (keyed by 'template:field_key')
            attachments: brushCheck.buildAttachmentsArray()
          };
        }

        // Fallback hard-coded form
        const { step_attachments, ...checkData } = formData.brush_robot_check;
        const allAttachments = [];
        Object.entries(step_attachments || {}).forEach(([stepKey, attachments]) => {
          attachments.forEach(att => {
            allAttachments.push({ ...att, step_key: stepKey });
          });
        });
        return { checkData, attachments: allAttachments };
      };

      const brushPayload = requiresBrushRobotCheck ? buildBrushRobotCheckPayload() : null;

      const buildSprayRobotCheckPayload = () => {
        // If using template-driven renderer, include template metadata and answers
        if (useSprayTemplate && sprayTemplateMetadata) {
          return {
            checkData: sprayCheck.buildCheckData({ ...formData.spray_robot_check }),
            // Template photo attachments (keyed by 'template:field_key')
            attachments: sprayCheck.buildAttachmentsArray()
          };
        }

        // Fallback: use hard-coded form data
        const { step_attachments, ...checkData } = formData.spray_robot_check;
        const allAttachments = [];
        Object.entries(step_attachments || {}).forEach(([stepKey, attachments]) => {
          attachments.forEach(att => {
            allAttachments.push({ ...att, step_key: stepKey });
          });
        });
        return { checkData, attachments: allAttachments };
      };

      const sprayPayload = requiresSprayRobotCheck ? buildSprayRobotCheckPayload() : null;

      // Build generic template_checks array for non-Brush/non-Spray assets
      // Only include if a generic template is actively being used (not 404, not 409 warning)
      const buildGenericTemplateChecksPayload = () => {
        if (!useGenericTemplate || !genericTemplate) {
          return null;
        }
        return [genericCheck.buildTemplateCheckEntry(ticket?.asset_id)];
      };

      const templateChecksPayload = buildGenericTemplateChecksPayload();

      const payload = {
        log_type: formData.log_type,
        // Site-work timings come from Job Stages — derived server-side.
        started_at: isSiteWork ? undefined : new Date(formData.started_at).toISOString(),
        work_period: formData.work_period,
        notes: formData.notes || null,
        is_on_call_callout: formData.is_on_call_callout || false,
        travel_record_id: travelRecord?.travel_id || undefined,
        attachments: formData.attachments,
        daily_van_check: isSiteWork ? buildDailyVanCheckPayload() : null,
        brush_robot_check: brushPayload ? brushPayload.checkData : null,
        brush_robot_check_attachments: brushPayload ? brushPayload.attachments : null,
        spray_robot_check: sprayPayload ? sprayPayload.checkData : null,
        spray_robot_check_attachments: sprayPayload ? sprayPayload.attachments : null,
        template_checks: templateChecksPayload
      };
      if (!isSiteWork && formData.ended_at) {
        payload.ended_at = new Date(formData.ended_at).toISOString();
      }
      // Fault codes belong to this work log whatever the outcome (Gareth
      // 2026-09-11) — sent on an in-progress save and a handover too, not just
      // a close. Parsed here so what is sent matches the chips that were shown.
      payload.fault_codes = parseFaultCodes(formData.fault_codes);
      // Create only: each co-engineer gets their own linked log server-side.
      if (!isEditing && formData.additional_engineer_ids.length > 0) {
        payload.additional_engineer_ids = formData.additional_engineer_ids;
      }
      if (formData.second_van?.vehicle_id) {
        payload.second_van = formData.second_van;
      }
      if (formData.outcome) {
        payload.outcome = formData.outcome;
        payload.assign_next_type = formData.assign_next_type || null;
        payload.assigned_to_next_id = formData.assigned_to_next_id || null;
        payload.resolution_summary = formData.resolution_summary || null;
        // customer_satisfied stays out of the form (2026-08-12).
        payload.create_follow_up_ticket = formData.follow_up_ticket_required || undefined;
        payload.follow_up_subject = formData.follow_up_subject || undefined;
        payload.follow_up_description = formData.follow_up_description || undefined;
        payload.follow_up_priority = formData.follow_up_priority || undefined;
      }
      if (formData.remote_session_id) {
        payload.remote_session_id = formData.remote_session_id;
      }
      // Parts used go with the work log, on a new log and a continued one,
      // and are recorded in its transaction. Only when there are lines, so a
      // save with no parts is exactly what it was. No prices are sent.
      if (spareLinesToSend.length > 0) {
        payload.parts = spareLinesToSend.map(({ key, value }) => ({
          line_id: key,
          catalogue_id: value.catalogue_id,
          item_number: value.item_number,
          name: value.name,
          quantity: Number(value.quantity),
          not_in_list: value.not_in_list,
          added_at: value.added_at
        }));
      }

      // Park rather than apply: the log stays in progress (no outcome), and
      // the close it would have performed rides along as pending_close.
      if (parkForHome) {
        payload.pending_close = {
          outcome: payload.outcome,
          assign_next_type: payload.assign_next_type,
          assigned_to_next_id: payload.assigned_to_next_id,
          resolution_summary: payload.resolution_summary,
          create_follow_up_ticket: payload.create_follow_up_ticket,
          follow_up_subject: payload.follow_up_subject,
          follow_up_description: payload.follow_up_description,
          follow_up_priority: payload.follow_up_priority
        };
        delete payload.outcome;
        delete payload.assign_next_type;
        delete payload.assigned_to_next_id;
        delete payload.resolution_summary;
        delete payload.create_follow_up_ticket;
        delete payload.follow_up_subject;
        delete payload.follow_up_description;
        delete payload.follow_up_priority;
      }

      const response = await authFetch(
        isEditing
          ? `/api/v1/tickets/${ticketId}/work-log/${workLog.log_id}`
          : `/api/v1/tickets/${ticketId}/work-log`,
        {
          method: isEditing ? 'PATCH' : 'POST',
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        // B4: the API says when a call-out lands in a month that has already
        // been paid, and where the money goes instead. The response used to be
        // thrown away, so the engineer never saw it.
        const saved = await response.json().catch(() => ({}));
        // The parts were recorded with the work log: the phone's copy is done with.
        forgetTicketSpares(sparesKey);
        onSaved(saved.payroll_notice || null);
      } else {
        // Nothing was saved, parts included, so the lines stay with the reason.
        const data = await response.json();
        setError(data.message || 'Failed to save work log');
      }
    } catch (err) {
      setError('Failed to save work log');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content work-log-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Continue Work Log' : 'Log Work'}</h2>
          {/* Not while saving: the answer decides whether the Parts used
              lines are done with or stay with the reason, and the page still
              needs the work log's pay notice. */}
          <button
            className="modal-close"
            disabled={saving}
            onClick={closeWindow}
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" ref={modalBodyRef}>
            {error && <div className="modal-error">{error}</div>}
            {(ticket?.customer_reported_fault || ticket?.description) && (
              <div className="worklog-ticket-context">
                <span>Customer reported</span>
                <p>{ticket.customer_reported_fault || ticket.description}</p>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="log_type">Work type *</label>
                <select
                  id="log_type"
                  name="log_type"
                  value={formData.log_type}
                  onChange={handleChange}
                  required
                >
                  {(config.work_types || []).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="work_period">Work Period *</label>
                <select
                  id="work_period"
                  name="work_period"
                  value={formData.work_period}
                  onChange={handleChange}
                  required
                >
                  {(config.work_periods || []).map(period => (
                    <option key={period.value} value={period.value}>{period.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {!isSiteWork && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="started_at">Started *</label>
                  <input
                    type="datetime-local"
                    id="started_at"
                    name="started_at"
                    value={formData.started_at}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}
            {isSiteWork && (
              <div className="modal-hint stage-times-note">
                Times come from Job Stages: started {stageStampTime(travelRecord?.departed_at)} — finished {stageStampTime(travelRecord?.returned_at || travelRecord?.departed_site_at)}
              </div>
            )}

            {/* Start stamps live at the TOP — tapped before the long check
                forms; the finish stamps sit next to the Outcome below
                (Gareth 2026-08-12). */}
            {isSiteWork && (
              <JobStagesCard
                authFetch={authFetch}
                ticketId={ticketId}
                workLogId={workLog?.log_id}
                travelRecord={travelRecord}
                onTravelRecordChange={setTravelRecord}
                visibleStages={['departed', 'arrived']}
                sectionTitle="Job Stages — start"
                sectionHint="Stamp as they happen - your live status and location update immediately. Times can be entered manually if you had no signal."
              />
            )}

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
              />
            </div>

            {/* Fault codes (Gareth 2026-09-11). Deliberately OUT of the closing
                panel and above the outcome: optional, available at any work
                type and at any point in the job, because codes are found during
                the work and the next person needs them. They belong to THIS
                log; previously-found codes show above as read-only context so
                nobody re-saves someone else's findings as their own. */}
            <div className="form-group">
              {priorFaultCodes.length > 0 && (
                <div className="prior-fault-codes">
                  <span className="prior-fault-codes-label">
                    {workLog?.log_id ? 'Also found on this ticket:' : 'Already found on this ticket:'}
                  </span>
                  <span className="fault-code-chips">
                    {priorFaultCodes.map(code => (
                      <span key={code} className="fault-code-chip">{code}</span>
                    ))}
                  </span>
                </div>
              )}
              <label htmlFor="fault_codes">Fault codes</label>
              <input
                id="fault_codes"
                name="fault_codes"
                type="text"
                value={formData.fault_codes}
                onChange={handleChange}
                placeholder="e.g. E101, E205 — separate multiple codes with commas"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
              />
              {parsedFaultCodes.length > 0 && (
                <div className="fault-code-chips">
                  {parsedFaultCodes.map(code => (
                    <span key={code} className="fault-code-chip">{code}</span>
                  ))}
                </div>
              )}
              {/* Published bulletins sharing a code (engineer bulletins Phase 3;
                  Gareth 2026-09-11: "if these are entered on log work, the
                  link to this bulletin can appear under neat"). Editing a log
                  starts with its own codes in the box, so those are looked up too. */}
              <RelatedBulletins codes={parsedFaultCodes} authFetch={authFetch} />
              <span className="field-help">
                Optional. Codes found during this piece of work — recorded so faults can be reported on by code.
              </span>
            </div>

            {/* Other engineers on this job (Gareth 2026-09-11). Create only:
                each one gets their OWN linked work log with these same times,
                so all the time on the ticket is captured and each is paid on
                their own rates and their own overtime rules. Editing an
                existing log does not re-open this — they own their own entry. */}
            {!isEditing && coEngineerOptions.length > 0 && (
              <div className="form-group">
                <label htmlFor="co_engineer_picker">Other engineers on this job</label>
                {/* A dropdown and Add, then the list with a remove button each
                    (Gareth 2026-09-14: the tick-box chips looked clunky). */}
                <EngineerPicker
                  id="co_engineer_picker"
                  options={coEngineerOptions}
                  selectedIds={formData.additional_engineer_ids}
                  onChange={ids => setFormData(prev => ({ ...prev, additional_engineer_ids: ids }))}
                />
                <span className="field-help">
                  {formData.additional_engineer_ids.length > 0
                    ? `${formData.additional_engineer_ids.length + 1} engineers on this job — each gets their own work log with these times, which they can adjust if they finished at a different time.`
                    : 'Optional. Anyone else who worked this job gets their own work log with these times.'}
                </span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="attachments">Attachments</label>
              <input id="attachments" type="file" multiple onChange={handleChange} />
              {formData.attachments.length > 0 && (
                <div className="modal-hint">{formData.attachments.length} attachment filename(s) captured</div>
              )}
            </div>

            {isSiteWork && (
              <div className="form-section daily-van-check-section">
                <div className="form-section-header">
                  <h3>Daily Van Check</h3>
                  <p>Required when Work Type is Site Work. Full detail is stored against the vehicle history.</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="vehicle_id">Vehicle *</label>
                    <select
                      id="vehicle_id"
                      value={formData.daily_van_check.vehicle_id}
                      onChange={event => handleVehicleChange(event.target.value)}
                      required={isSiteWork}
                    >
                      <option value="">{vehiclesLoading ? 'Loading vehicles...' : 'Select vehicle...'}</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                          {vehicle.registration_number} ({vehicle.number_of_seats} seats)
                          {vehicle.current_odometer_reading !== null && vehicle.current_odometer_reading !== undefined
                            ? ` - ${Number(vehicle.current_odometer_reading).toLocaleString()} mi`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="current_odometer_reading">Current odometer reading *</label>
                    <input
                      id="current_odometer_reading"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.daily_van_check.current_odometer_reading}
                      onChange={event => updateDailyVanCheck('current_odometer_reading', event.target.value)}
                      required={isSiteWork}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Employees travelling *</label>
                  {!formData.daily_van_check.vehicle_id ? (
                    <div className="modal-hint">Select a vehicle to choose employees travelling.</div>
                  ) : (
                    <div className="seat-selection-grid">
                      {Array.from({ length: seatCount }, (_, idx) => (
                        <div key={idx} className="seat-selection-row">
                          <label htmlFor={`seat_${idx}`}>Seat {idx + 1}</label>
                          <select
                            id={`seat_${idx}`}
                            value={formData.daily_van_check.seat_selections?.[idx] || ''}
                            onChange={event => updateSeatSelection(idx, event.target.value)}
                            className={isEmployeeAlreadySelected(formData.daily_van_check.seat_selections?.[idx], idx) ? 'duplicate-warning' : ''}
                          >
                            <option value="">Empty</option>
                            {vanCrew.map(engineer => (
                              <option
                                key={engineer.user_id}
                                value={engineer.user_id}
                                disabled={isEmployeeAlreadySelected(engineer.user_id, idx)}
                              >
                                {engineer.full_name}
                                {isEmployeeAlreadySelected(engineer.user_id, idx) ? ' (already selected)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                      {getSelectedEmployeeIds().length === 0 && (
                        <div className="modal-hint warning">At least one employee must be selected.</div>
                      )}
                    </div>
                  )}

                  {/* More engineers than seats means a second van (Gareth
                      2026-09-11), checked by whichever of them drove it. The
                      server refuses the save without this, so it is offered
                      here rather than letting someone hit a 400 on site. */}
                  {needsSecondVan && (
                    <div className="second-van-panel">
                      <div className="modal-hint warning">
                        {vanCrew.length} engineers on this job, and {selectedVehicle?.registration_number || 'this van'} seats {seatCount}.
                        {' '}Choose a second van for the other {vanCrew.length - seatCount}.
                      </div>
                      <div className="form-group">
                        <label htmlFor="second_van_vehicle">Second van *</label>
                        <select
                          id="second_van_vehicle"
                          value={formData.second_van?.vehicle_id || ''}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            second_van: { ...(prev.second_van || {}), vehicle_id: e.target.value }
                          }))}
                        >
                          <option value="">Select a van...</option>
                          {vehicles
                            .filter(v => v.vehicle_id !== formData.daily_van_check.vehicle_id)
                            .map(v => (
                              <option key={v.vehicle_id} value={v.vehicle_id}>
                                {v.registration_number}{v.number_of_seats ? ` (${v.number_of_seats} seats)` : ''}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="second_van_driver">Who drove it *</label>
                        <select
                          id="second_van_driver"
                          value={formData.second_van?.driver_id || ''}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            second_van: { ...(prev.second_van || {}), driver_id: e.target.value }
                          }))}
                        >
                          <option value="">Select the driver...</option>
                          {vanCrew
                            .filter(e => !getSelectedEmployeeIds().includes(e.user_id))
                            .map(e => (
                              <option key={e.user_id} value={e.user_id}>{e.full_name}</option>
                            ))}
                        </select>
                        <span className="field-help">
                          They complete the second van&apos;s check — unless they already checked that van earlier today.
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                  <div className="van-check-grid">
                    {DAILY_VAN_CHECK_BOOLEAN_FIELDS.map(([field, label]) => (
                      <div key={field} className="form-group">
                        <label htmlFor={field}>{label} *</label>
                        <select
                          id={field}
                          value={formData.daily_van_check[field]}
                          onChange={event => updateDailyVanCheck(field, event.target.value)}
                          required={isSiteWork}
                        >
                          <option value="">Select...</option>
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </div>
                    ))}
                  </div>

                  {formData.daily_van_check.warning_lights_messages === 'true' && (
                    <div className="form-group">
                      <label htmlFor="warning_notes">Warning notes *</label>
                      <textarea
                        id="warning_notes"
                        value={formData.daily_van_check.warning_notes}
                        onChange={event => updateDailyVanCheck('warning_notes', event.target.value)}
                        rows={2}
                        required
                      />
                    </div>
                  )}

                  {formData.daily_van_check.damage === 'true' && (
                    <div className="conditional-panel">
                      <div className="form-group">
                        <label htmlFor="damage_description">Damage description *</label>
                        <textarea
                          id="damage_description"
                          value={formData.daily_van_check.damage_description}
                          onChange={event => updateDailyVanCheck('damage_description', event.target.value)}
                          rows={2}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="damage_photo">Damage photos *</label>
                        <input
                          id="damage_photo"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={event => handleDamagePhotoChange(event.target.files, event.target)}
                          required={formData.daily_van_check.damage_photo_attachments.length === 0}
                        />
                        <div className="modal-hint">
                          Add as many photos as needed — each pick adds to the set.
                        </div>
                        {formData.daily_van_check.damage_photo_attachments.length > 0 && (
                          <div className="robot-check-photos">
                            {formData.daily_van_check.damage_photo_attachments.map((att, idx) => (
                              <PhotoThumb
                                key={att.attachment_id || `${att.name}-${idx}`}
                                att={att}
                                onRemove={() => removeDamagePhoto(idx)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                <div className="form-group">
                  <label htmlFor="additional_information">Additional information</label>
                  <textarea
                    id="additional_information"
                    value={formData.daily_van_check.additional_information}
                    onChange={event => updateDailyVanCheck('additional_information', event.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}

            {requiresBrushRobotCheck && (useBrushTemplate || useBrushFallback || brushTemplateLoading) && (
              <div className="form-section brush-robot-check-section">
                <div className="form-section-header">
                  <h3>{brushFormName}</h3>
                  <p>{isRemoteWork ? 'Complete this remote diagnostic check.' : 'Complete this check before leaving site.'}</p>
                </div>

                {/* Template loading state */}
                {brushTemplateLoading && (
                  <div className="template-loading-note">
                    <span>Loading template...</span>
                  </div>
                )}

                {/* Resolve warning if fallback was used */}
                {brushResolveWarning && (
                  <div className="template-resolve-warning">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span>{brushResolveWarning}</span>
                  </div>
                )}

                {/* Template-driven renderer (display only) */}
                {useBrushTemplate && (
                  <>
                    <div className="template-version-note">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                      <span>Using {brushFormName} template v{brushTemplate.version}.</span>
                    </div>
                    <TemplateCheckFormRenderer
                      schema={brushTemplate.schema_json}
                      answers={brushTemplateAnswers}
                      onAnswersChange={setBrushTemplateAnswers}
                      disabled={false}
                      stepAttachments={brushTemplateAttachments}
                      onAddPhotos={addBrushTemplateAttachments}
                      onRemovePhoto={removeBrushTemplateAttachment}
                    />
                  </>
                )}

                {/* Fallback hard-coded form */}
                {useBrushFallback && (
                  <>
                    <div className="template-fallback-note">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span>Using fallback Brush Robot Check form because the active template could not be loaded.</span>
                    </div>
                    <div className="robot-check-grid">
                      <div className="robot-check-item with-photo">
                        <div className="robot-check-item-main">
                          <label>Is robot cell guarding secure and damage free?</label>
                          <div className="yes-no-select">
                            <select
                              value={formData.brush_robot_check.cell_guarding_secure}
                              onChange={e => updateBrushRobotCheck('cell_guarding_secure', e.target.value)}
                            >
                              <option value="">Not checked</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          </div>
                        </div>
                        <div className="robot-check-photos">
                          <label className="photo-upload-btn">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={e => addBrushRobotCheckStepAttachments('cell_guarding_secure', e.target.files, e.target)}
                            />
                            + Photo
                          </label>
                          {(formData.brush_robot_check.step_attachments?.cell_guarding_secure || []).map((att, idx) => (
                            <PhotoThumb
                              key={att.attachment_id || idx}
                              att={att}
                              onRemove={() => removeBrushRobotCheckStepAttachment('cell_guarding_secure', idx)}
                            />
                          ))}
                        </div>
                      </div>

                      {formData.brush_robot_check.cell_guarding_secure === 'false' && (
                        <div className="robot-check-item full-width">
                          <label>What are the issues with the robot cell? {hasOutcome && '*'}</label>
                          <textarea
                            value={formData.brush_robot_check.cell_issues}
                            onChange={e => updateBrushRobotCheck('cell_issues', e.target.value)}
                            rows={2}
                            placeholder="Describe the issues..."
                          />
                        </div>
                      )}

                      {BRUSH_ROBOT_CHECK_FIELDS.slice(1).map(([field, label, invertedLogic]) => (
                        <div key={field} className="robot-check-item with-photo">
                          <div className="robot-check-item-main">
                            <label>{label}</label>
                            <div className="yes-no-select">
                              <select
                                value={formData.brush_robot_check[field]}
                                onChange={e => updateBrushRobotCheck(field, e.target.value)}
                              >
                                <option value="">Not checked</option>
                                <option value="true">{invertedLogic ? 'Yes (issue)' : 'Yes'}</option>
                                <option value="false">{invertedLogic ? 'No (OK)' : 'No'}</option>
                              </select>
                            </div>
                          </div>
                          <div className="robot-check-photos">
                            <label className="photo-upload-btn">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={e => addBrushRobotCheckStepAttachments(field, e.target.files, e.target)}
                              />
                              + Photo
                            </label>
                            {(formData.brush_robot_check.step_attachments?.[field] || []).map((att, idx) => (
                              <PhotoThumb
                                key={att.attachment_id || idx}
                                att={att}
                                onRemove={() => removeBrushRobotCheckStepAttachment(field, idx)}
                              />
                            ))}
                          </div>
                        </div>
                      ))}

                      <div className="robot-check-item full-width with-photo">
                        <div className="robot-check-item-main">
                          <label>Are there any other issues found with the system as a whole?</label>
                          <textarea
                            value={formData.brush_robot_check.other_issues}
                            onChange={e => updateBrushRobotCheck('other_issues', e.target.value)}
                            rows={2}
                            placeholder="Describe any other issues..."
                          />
                        </div>
                        <div className="robot-check-photos">
                          <label className="photo-upload-btn">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={e => addBrushRobotCheckStepAttachments('other_issues', e.target.files, e.target)}
                            />
                            + Photo
                          </label>
                          {(formData.brush_robot_check.step_attachments?.other_issues || []).map((att, idx) => (
                            <PhotoThumb
                              key={att.attachment_id || idx}
                              att={att}
                              onRemove={() => removeBrushRobotCheckStepAttachment('other_issues', idx)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Only when the template does NOT carry its own Completion
                    field - otherwise the tick lives inside the check form */}
                {!brushCompletionInTemplate && (
                  <div className="form-group robot-check-confirm">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.brush_robot_check.check_completed}
                        onChange={e => updateBrushRobotCheck('check_completed', e.target.checked)}
                      />
                      {useBrushTemplate ? `${brushFormName} complete` : 'Pre Robot Check Complete'} {hasOutcome && '*'}
                    </label>
                    {!hasOutcome && (
                      <span className="modal-hint">Required when completing this work log with an outcome.</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Non-site-work 409 warning for Brush - show separately when no template/fallback available */}
            {requiresBrushRobotCheck && !isSiteWork && brushResolveWarning && !useBrushTemplate && !brushTemplateLoading && (
              <div className="form-section brush-robot-check-section">
                <div className="form-section-header">
                  <h3>{brushFormName}</h3>
                  <p>Template targeting issue - check not available.</p>
                </div>
                <div className="template-resolve-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>{brushResolveWarning}</span>
                </div>
              </div>
            )}

            {requiresSprayRobotCheck && (useSprayTemplate || useSprayFallback || sprayTemplateLoading) && (
              <div className="form-section spray-robot-check-section">
                <div className="form-section-header">
                  <h3>{sprayFormName}</h3>
                  <p>{isRemoteWork ? 'Complete this remote diagnostic check.' : 'Complete this check before leaving site.'}</p>
                </div>

                {/* Template loading state */}
                {sprayTemplateLoading && (
                  <div className="template-loading-note">
                    <span>Loading template...</span>
                  </div>
                )}

                {/* Resolve warning if fallback was used */}
                {sprayResolveWarning && (
                  <div className="template-resolve-warning">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span>{sprayResolveWarning}</span>
                  </div>
                )}

                {/* Template-driven renderer */}
                {useSprayTemplate && (
                  <>
                    <div className="template-version-note">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                      <span>Using {sprayFormName} template v{sprayTemplate.version}.</span>
                    </div>
                    <TemplateCheckFormRenderer
                      schema={sprayTemplate.schema_json}
                      answers={sprayTemplateAnswers}
                      onAnswersChange={setSprayTemplateAnswers}
                      disabled={false}
                      stepAttachments={sprayTemplateAttachments}
                      onAddPhotos={addSprayTemplateAttachments}
                      onRemovePhoto={removeSprayTemplateAttachment}
                    />
                  </>
                )}

                {/* Fallback hard-coded form */}
                {useSprayFallback && (
                  <>
                    <div className="template-fallback-note">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span>Using fallback Spray Robot Check form because the active template could not be loaded.</span>
                    </div>
                    <div className="robot-check-grid">
                      <div className="robot-check-item with-photo">
                        <div className="robot-check-item-main">
                          <label>Is robot cell guarding secure and damage free?</label>
                          <div className="yes-no-select">
                            <select
                              value={formData.spray_robot_check.cell_guarding_secure}
                              onChange={e => updateSprayRobotCheck('cell_guarding_secure', e.target.value)}
                            >
                              <option value="">Not checked</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          </div>
                        </div>
                        <div className="robot-check-photos">
                          <label className="photo-upload-btn">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={e => addSprayRobotCheckStepAttachments('cell_guarding_secure', e.target.files, e.target)}
                            />
                            + Photo
                          </label>
                          {(formData.spray_robot_check.step_attachments?.cell_guarding_secure || []).map((att, idx) => (
                            <PhotoThumb
                              key={att.attachment_id || idx}
                              att={att}
                              onRemove={() => removeSprayRobotCheckStepAttachment('cell_guarding_secure', idx)}
                            />
                          ))}
                        </div>
                      </div>

                      {formData.spray_robot_check.cell_guarding_secure === 'false' && (
                        <div className="robot-check-item full-width">
                          <label>What are the issues with the robot cell? {hasOutcome && '*'}</label>
                          <textarea
                            value={formData.spray_robot_check.cell_issues}
                            onChange={e => updateSprayRobotCheck('cell_issues', e.target.value)}
                            rows={2}
                            placeholder="Describe the issues..."
                          />
                        </div>
                      )}

                      {SPRAY_ROBOT_CHECK_FIELDS.slice(1).map(([field, label, invertedLogic]) => (
                        <div key={field} className="robot-check-item with-photo">
                          <div className="robot-check-item-main">
                            <label>{label}</label>
                            <div className="yes-no-select">
                              <select
                                value={formData.spray_robot_check[field]}
                                onChange={e => updateSprayRobotCheck(field, e.target.value)}
                              >
                                <option value="">Not checked</option>
                                <option value="true">{invertedLogic ? 'Yes (issue)' : 'Yes'}</option>
                                <option value="false">{invertedLogic ? 'No (OK)' : 'No'}</option>
                              </select>
                            </div>
                          </div>
                          <div className="robot-check-photos">
                            <label className="photo-upload-btn">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={e => addSprayRobotCheckStepAttachments(field, e.target.files, e.target)}
                              />
                              + Photo
                            </label>
                            {(formData.spray_robot_check.step_attachments?.[field] || []).map((att, idx) => (
                              <PhotoThumb
                                key={att.attachment_id || idx}
                                att={att}
                                onRemove={() => removeSprayRobotCheckStepAttachment(field, idx)}
                              />
                            ))}
                          </div>
                        </div>
                      ))}

                      <div className="robot-check-item full-width with-photo">
                        <div className="robot-check-item-main">
                          <label>Are there any other issues found with the system as a whole?</label>
                          <textarea
                            value={formData.spray_robot_check.other_issues}
                            onChange={e => updateSprayRobotCheck('other_issues', e.target.value)}
                            rows={2}
                            placeholder="Describe any other issues..."
                          />
                        </div>
                        <div className="robot-check-photos">
                          <label className="photo-upload-btn">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={e => addSprayRobotCheckStepAttachments('other_issues', e.target.files, e.target)}
                            />
                            + Photo
                          </label>
                          {(formData.spray_robot_check.step_attachments?.other_issues || []).map((att, idx) => (
                            <PhotoThumb
                              key={att.attachment_id || idx}
                              att={att}
                              onRemove={() => removeSprayRobotCheckStepAttachment('other_issues', idx)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {!sprayCompletionInTemplate && (
                  <div className="form-group robot-check-confirm">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.spray_robot_check.check_completed}
                        onChange={e => updateSprayRobotCheck('check_completed', e.target.checked)}
                      />
                      {useSprayTemplate ? `${sprayFormName} complete` : 'Post Robot Check Complete'} {hasOutcome && '*'}
                    </label>
                    {!hasOutcome && (
                      <span className="modal-hint">Required when completing this work log with an outcome.</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Non-site-work 409 warning for Spray - show separately when no template/fallback available */}
            {requiresSprayRobotCheck && !isSiteWork && sprayResolveWarning && !useSprayTemplate && !sprayTemplateLoading && (
              <div className="form-section spray-robot-check-section">
                <div className="form-section-header">
                  <h3>{sprayFormName}</h3>
                  <p>Template targeting issue - check not available.</p>
                </div>
                <div className="template-resolve-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>{sprayResolveWarning}</span>
                </div>
              </div>
            )}

            {/* Generic template check for non-Brush/non-Spray assets */}
            {useGenericTemplate && (
              <div className="form-section generic-check-section">
                <div className="form-section-header">
                  <h3>{genericTemplate.name || 'Asset Check'}</h3>
                  <p>Complete this check for the asset.</p>
                </div>
                <div className="template-version-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span>
                    {savedGenericTemplateCheck
                      ? `Using saved template v${genericTemplate.version} (snapshot).`
                      : `Using template v${genericTemplate.version}.`
                    }
                  </span>
                </div>
                <TemplateCheckFormRenderer
                  schema={genericTemplate.schema_json}
                  answers={genericTemplateAnswers}
                  onAnswersChange={setGenericTemplateAnswers}
                  disabled={false}
                  stepAttachments={genericTemplateAttachments}
                  onAddPhotos={addGenericTemplateAttachments}
                  onRemovePhoto={removeGenericTemplateAttachment}
                />
                {!genericCompletionInTemplate && (
                  <div className="form-group robot-check-confirm">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={genericCheckCompleted}
                        onChange={e => setGenericCheckCompleted(e.target.checked)}
                      />
                      Check Complete {hasOutcome && '*'}
                    </label>
                    {!hasOutcome && (
                      <span className="modal-hint">Required when completing this work log with an outcome.</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Loading state for generic template */}
            {canHaveGenericCheck && genericTemplateLoading && (
              <div className="form-section generic-check-section">
                <div className="form-section-header">
                  <h3>Asset Check</h3>
                  <p>Loading template...</p>
                </div>
                <div className="template-loading-note">
                  <span>Loading template...</span>
                </div>
              </div>
            )}

            {/* 409 warning for generic check - show when targeting is ambiguous */}
            {showGenericWarning && (
              <div className="form-section generic-check-section">
                <div className="form-section-header">
                  <h3>Asset Check</h3>
                  <p>Template targeting issue - check not available.</p>
                </div>
                <div className="template-resolve-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>{genericResolveWarning}</span>
                </div>
              </div>
            )}

            {/* Finish stamps sit next to the Outcome — stamped at the same
                real-world moment (Gareth 2026-08-12). */}
            {isSiteWork && (
              <JobStagesCard
                authFetch={authFetch}
                ticketId={ticketId}
                workLogId={workLog?.log_id}
                travelRecord={travelRecord}
                onTravelRecordChange={setTravelRecord}
                visibleStages={['departed_site', 'returned']}
                skipLoad
                sectionTitle="Job Stages — finish"
                sectionHint={FINISH_STAGES_HINT}
              />
            )}

            <div className="form-section completion-section">
              <div className="form-section-header compact">
                <h3>Completion</h3>
                <p>
                  {isSiteWork
                    ? 'Leave outcome blank to save progress. Times come from the Job Stages stamps.'
                    : 'Leave outcome blank to save progress. Select an outcome and finish time to complete this work log.'}
                </p>
              </div>
              <WorkLogOutcomeSection
                config={config}
                engineers={engineers}
                allowInProgress
                closesLabel="Closes the ticket"
                summaryLabel="Resolution summary"
                summaryPlaceholder="What fixed the customer issue?"
                followUpPlaceholder={`Fault found during ${ticket?.ticket_ref || 'this ticket'}`}
                notesProvided={Boolean(formData.notes?.trim())}
                finishTimeSlot={!isSiteWork ? (
                  <div className="form-group">
                    <label htmlFor="ended_at">Finish time {hasOutcome ? '*' : ''}</label>
                    <input
                      type="datetime-local"
                      id="ended_at"
                      name="ended_at"
                      value={formData.ended_at}
                      onChange={handleChange}
                      required={hasOutcome}
                    />
                    {isInProgress && (
                      <span className="modal-hint">Required only when completing this work log.</span>
                    )}
                  </div>
                ) : null}
                value={{
                  outcome: formData.outcome,
                  summary: formData.resolution_summary,
                  follow_up_ticket_required: formData.follow_up_ticket_required,
                  follow_up_subject: formData.follow_up_subject,
                  follow_up_description: formData.follow_up_description,
                  follow_up_priority: formData.follow_up_priority,
                  assign_next_type: formData.assign_next_type,
                  assigned_to_next_id: formData.assigned_to_next_id
                }}
                onChange={(patch) => setFormData(prev => ({
                  ...prev,
                  ...('summary' in patch ? { resolution_summary: patch.summary } : {}),
                  ...Object.fromEntries(Object.entries(patch).filter(([key]) => key !== 'summary'))
                }))}
              />
            </div>

            {/* Parts used (spare parts, Push 2): never inside a .form-group,
                whose 14px inputs make iPhones zoom. Only for roles that
                record parts; the ticket's own Parts used list is for everyone. */}
            {canRecordParts && (
            <div className="form-section spare-parts-section">
              <div className="form-section-header compact">
                <h3>Parts used</h3>
                <p>Add each part fitted. Parts are recorded when you save the work log.</p>
              </div>
              {restoredSpares.length > 0 && spareLines.length > 0 && (
                <div className="modal-hint" role="note">{RESTORED_SPARES_NOTE}</div>
              )}
              {isEditing && recordedPartsStatus === 'failed' && (
                <div className="modal-hint" role="status">{RECORDED_SPARES_UNREAD_NOTE}</div>
              )}
              {alreadyRecorded.length > 0 && (
                <div className="spare-parts-recorded">
                  <span className="spare-parts-recorded-label">Already recorded on this work log</span>
                  <ul>
                    {alreadyRecorded.map(part => (
                      <li key={part.part_id}>{spareLineLabel(part.part_name, part.part_number)} ×{quantityText(part.quantity_text ?? part.quantity)}</li>
                    ))}
                  </ul>
                </div>
              )}
              <SparePartsUsed
                id="ticket-spare-parts"
                lines={spareLines}
                robots={[]}
                catalogue={spareParts}
                showAddedBy={false}
                disabled={saving}
                onAdd={addSpareLine}
                onChange={changeSpareLine}
                onRemove={removeSpareLine}
                onQuantityProblem={(key, sentence) => { spareTextProblemsRef.current[key] = sentence || undefined; }}
              />
            </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" disabled={saving} onClick={closeWindow}>Cancel</button>
            {/* Only while the last Job Stages time is still to come: once it
                is recorded, Complete Work Log is the way. */}
            {isSiteWork && hasOutcome && !travelRecord?.returned_at && (
              <button
                type="button"
                className="btn-secondary"
                disabled={saving}
                onClick={(event) => handleSubmit(event, { parkForHome: true })}
                title={`Save everything now and close this from Today with one tap when you are home or leave for your next job (${ARRIVED_HOME_LABEL})`}
              >
                {saving ? 'Saving...' : 'Finish at home'}
              </button>
            )}
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : (isInProgress ? 'Save Progress' : 'Complete Work Log')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Builds a simple, editable draft email from ticket data. The engineer/admin
 * reviews and edits this before sending - it's a starting point, not a
 * structured template.
 */
function buildClosureEmailDraft(ticket) {
  const contactFirstName = (ticket.contact_name || '').split(' ')[0];
  const subject = `Your support ticket ${ticket.ticket_ref} has been resolved`;

  const lines = [
    `Dear ${contactFirstName || 'Customer'},`,
    '',
    `Thank you for contacting Intelligent Dairy Solutions regarding "${ticket.subject}".`,
    ''
  ];

  if (ticket.resolution_summary) {
    lines.push(ticket.resolution_summary, '');
  }
  if (ticket.root_cause) {
    lines.push(`Root cause: ${ticket.root_cause}`, '');
  }

  lines.push(
    "If you have any further questions, please don't hesitate to get in touch.",
    '',
    'Best regards,',
    'Intelligent Dairy Solutions'
  );

  return { subject, body: lines.join('\n') };
}

/**
 * Optional, manual closure email on the shared EmailDraftModal (R4c): the
 * draft is editable, validated + stamped server-side, then opened in the
 * user's own client with the CRM inbox CC'd. Never sent automatically.
 */
function EmailCustomerModal({ ticket, authFetch, onClose, onSent }) {
  return (
    <EmailDraftModal
      title="Email Customer"
      customerId={ticket.customer_id}
      authFetch={authFetch}
      onClose={onClose}
      defaultContactId={ticket.contact_id || ''}
      initialDraft={buildClosureEmailDraft(ticket)}
      actionLabel="Open Email Draft"
      onAction={async (contactId, { subject, body }) => {
        const response = await authFetch(`/api/v1/tickets/${ticket.ticket_id}/send-closure-email`, {
          method: 'POST',
          body: JSON.stringify({ contact_id: contactId, subject, body })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to prepare the email');
        // §0.8: open the user's own email client pre-filled, CRM inbox CC'd.
        if (data.draft) {
          openDraft(data.draft);
        }
        onSent(data);
      }}
    />
  );
}

export default TicketDetail;
